// src/NFTDashboard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { BrowserProvider, Contract, isAddress, JsonRpcSigner } from 'ethers';
import UpgradeModal from './UpgradeModal';
import RecipeModal from './RecipeModal';
import ErrorModal from './ErrorModal';
import { fetchOwnedNFTs } from './api';
import DealsSection from './DealsSection';
import FaucetModal from './FaucetModal';
import DCLPurchaseModal from './DCLPurchaseModal';
import walletManager from './utils/WalletManager';
import MintLaunchingPage from './MintLaunchingPage';
import { supabase } from './supabaseClient';

// MANA token contract on Polygon
const MANA_ADDRESS = '0xA1c57f48F0Deb89f569dFbE6E2B7f46D33606fD4';
const MANA_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)'
];

// DRIP token contract on Monad
const DRIP_ADDRESS = '0xEa3D455531cbD8Ef47444A77bB85b7D312317777';
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)'
];

// Lookup Tool Component
function LookupTool({ wallet, collectionItems, upgrades, deals, faucetConfig, faucetStatus, onError }) {
  const [lookupUrl, setLookupUrl] = useState('');
  const [lookupResults, setLookupResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to get display type for lookup results
  const getDisplayType = (item, isRecipe = false) => {
    if (isRecipe) {
      if (item.recipeType === 'free_mint') return 'FREE MINT';
      if (item.recipeType === 'token_gated') return 'RECIPE UPGRADE';
      return 'RECIPE UPGRADE';
    } else {
      // Deal types - don't expose sensitive discount info in lookup
      if (item.dealType === 'dcl_wearable') return 'DCL WEARABLE DEAL';
      if (item.hasDiscountAccess) return 'DISCOUNT CODE'; // Only show if user has access
      return 'PHYGITAL DEAL';
    }
  };

  // Parse marketplace URLs to extract collection address and token ID
  const parseMarketplaceUrl = (url) => {
    try {
      // Trim whitespace and remove any trailing characters
      const cleanUrl = url.trim();
      const urlObj = new URL(cleanUrl);
      
      // Decentraland Marketplace
      if (urlObj.hostname.includes('decentraland.org') || urlObj.hostname.includes('market.decentraland.org')) {
        // Format: https://market.decentraland.org/contracts/0x.../tokens/123
        const pathMatch = urlObj.pathname.match(/\/contracts\/([^\/]+)\/tokens\/([^\/]+)/);
        if (pathMatch) {
          return {
            collection: pathMatch[1],
            tokenId: pathMatch[2],
            marketplace: 'Decentraland'
          };
        }
      }
      
      // OpenSea
      if (urlObj.hostname.includes('opensea.io')) {
        // Format 1: https://opensea.io/assets/matic/0x.../123
        let pathMatch = urlObj.pathname.match(/^\/assets\/[^\/]+\/([^\/]+)\/([^\/]+)$/);
        if (pathMatch) {
          return {
            collection: pathMatch[1],
            tokenId: pathMatch[2],
            marketplace: 'OpenSea'
          };
        }
        
        // Format 2: https://opensea.io/item/polygon/0x.../123
        pathMatch = urlObj.pathname.match(/^\/item\/[^\/]+\/([^\/]+)\/([^\/]+)$/);
        if (pathMatch) {
          return {
            collection: pathMatch[1],
            tokenId: pathMatch[2],
            marketplace: 'OpenSea'
          };
        }
        
        // Try more flexible matching without anchors as fallback
        pathMatch = urlObj.pathname.match(/\/item\/[^\/]+\/([^\/]+)\/([^\/]+)/);
        if (pathMatch) {
          return {
            collection: pathMatch[1],
            tokenId: pathMatch[2],
            marketplace: 'OpenSea'
          };
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Check eligibility for a specific NFT
  const checkNFTEligibility = async (collection, tokenId) => {
    // For lookup tool, allow checking without wallet but validate NFT exists
    const isLookupMode = !wallet;
    
    if (!isLookupMode && !wallet) {
      onError({
        title: 'Wallet Required',
        message: 'Please connect your wallet to check claim eligibility.',
        type: 'warning'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Try to fetch NFT metadata to verify it exists and get info
      let nftMetadata = null;
      let metadataFetchFailed = false;
      
      try {
        console.log('🔍 Fetching NFT metadata:', collection, tokenId);
        
        // Use the same API base URL as other calls
        const metadataResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/?action=metadata&collection=${encodeURIComponent(collection.toLowerCase())}&tokenId=${encodeURIComponent(tokenId)}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (metadataResponse.ok) {
          const data = await metadataResponse.json();
          if (data && (data.name || data.title || data.metadata?.name || data.metadata?.title)) {
            nftMetadata = data.metadata || data;
            console.log('✅ NFT metadata found:', nftMetadata.name || nftMetadata.title);
          } else if (data && data.metadata === null) {
            // API returned successfully but metadata is null (NFT doesn't exist or not minted)
            console.log('❌ NFT does not exist (metadata is null)');
            const errorResult = {
              nft: { collection, tokenId },
              isDOCTORdrippCollectible: false,
              nftInfo: null,
              freeMintsAvailable: [],
              dealsAvailable: [],
              faucetAvailable: null,
              error: 'This NFT does not exist or has not been minted yet. Please verify the URL and try again.'
            };
            
            setLookupResults(errorResult);
            setIsLoading(false);
            return;
          } else {
            console.log('⚠️ NFT metadata response empty or invalid:', data);
            metadataFetchFailed = true;
          }
        } else {
          console.log('⚠️ Metadata API returned error:', metadataResponse.status, metadataResponse.statusText);
          metadataFetchFailed = true;
        }
      } catch (error) {
        console.log('⚠️ Metadata fetch failed:', error.message);
        metadataFetchFailed = true;
      }
      
      // If metadata fetch failed, continue anyway - could be API issues, CORS, etc.
      // Let the user see what deals would be available if the NFT existed
      if (metadataFetchFailed) {
        console.log('⚠️ Continuing with lookup despite metadata fetch failure');
      }
      
      const results = {
        nft: { collection, tokenId },
        freeMintsAvailable: [],
        dealsAvailable: [],
        faucetAvailable: null,
        totalAvailable: 0,
        isDOCTORdrippCollectible: false,
        nftInfo: nftMetadata
      };

      // Check if this is a DOCTORdripp collectible
      const collectionItem = collectionItems.find(
        item => item.collection_address.toLowerCase() === collection.toLowerCase() && 
                item.token_id === tokenId
      );
      
      if (collectionItem) {
        results.isDOCTORdrippCollectible = true;
        results.nftInfo = {
          title: collectionItem.title,
          image: collectionItem.image,
          urnItemId: collectionItem.urn_item_id
        };
      } else {
        // Check if it's at least in our approved collections
        const isApprovedCollection = collectionItems.some(
          item => item.collection_address.toLowerCase() === collection.toLowerCase()
        );
        results.isDOCTORdrippCollectible = isApprovedCollection;
      }

      // Check free mint recipes
      for (const recipe of upgrades.filter(u => u.recipe_type === 'free_mint')) {
        // Check if this NFT would qualify for the recipe
        let qualifies = false;
        if (recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0) {
          for (const [index, gate] of recipe.token_gate_requirements.entries()) {
            const requiredCollection = gate.collection.toLowerCase();
            if (collection.toLowerCase() !== requiredCollection) continue;
            
            const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
            if (requiredItemId && tokenId !== requiredItemId) continue;
            
            // Check URN requirements
            const urnRequirement = recipe.token_gate_urn_requirements && recipe.token_gate_urn_requirements[index];
            const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
            
            if (requiredUrnItemId) {
              // For lookup tool, we'll let the backend handle URN validation with dynamic detection
              // Skip the frontend URN check since we might not have the collectionItem for blockchain token IDs
              console.log('🔍 Recipe has URN requirement, will validate via backend:', requiredUrnItemId);
            }
            
            qualifies = true;
            break;
          }
        }

        if (qualifies) {
          // Get claim statistics for this recipe
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipe-validation?action=get-stats`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                recipeId: recipe.id,
                walletAddress: wallet,
                qualifyingTokenIds: [tokenId]
              })
            });

            if (response.ok) {
              const stats = await response.json();
              const walletRemaining = Math.max(0, (stats.walletLimit || 0) - (stats.walletUsed || 0));
              const tokenRemaining = stats.perTokenEnabled 
                ? Math.max(0, (stats.perTokenLimit || 1) - Math.min(stats.totalTokenUsed || 0, stats.perTokenLimit || 1))
                : walletRemaining;
              
              // For lookup tool, prioritize token-specific remaining uses like deals do
              const remaining = stats.perTokenEnabled ? tokenRemaining : walletRemaining;
              
              results.freeMintsAvailable.push({
                title: recipe.title,
                remaining,
                walletRemaining,
                tokenRemaining: stats.perTokenEnabled ? tokenRemaining : null,
                walletUsed: stats.walletUsed || 0,
                walletLimit: stats.walletLimit || 0,
                tokenUsed: stats.perTokenEnabled ? (stats.totalTokenUsed || 0) : null,
                tokenLimit: stats.perTokenEnabled ? (stats.perTokenLimit || 1) : null,
                rewards: recipe.rewards || [], // Add rewards data for display
                recipeType: recipe.recipe_type || 'recipe' // Add recipe type for display
              });
              
              if (remaining > 0) {
                results.totalAvailable += remaining;
              }
            }
          } catch (error) {
            console.error('Error checking recipe stats:', error);
            // Still add the recipe even if stats failed
            results.freeMintsAvailable.push({
              title: recipe.title,
              remaining: 0,
              walletRemaining: 0,
              tokenRemaining: null,
              walletUsed: '?',
              walletLimit: '?',
              tokenUsed: null,
              tokenLimit: null,
              error: 'Failed to load stats',
              rewards: recipe.rewards || [], // Add rewards data for display
              recipeType: recipe.recipe_type || 'recipe' // Add recipe type for display
            });
          }
        }
      }

      // Check deals (similar logic)
      for (const deal of deals.filter(d => d.active)) {
        let qualifies = false;
        if (deal.required_items && deal.required_items.length > 0) {
          for (const [index, gate] of deal.required_items.entries()) {
            const requiredCollection = gate.collection.toLowerCase();
            if (collection.toLowerCase() !== requiredCollection) continue;
            
            const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
            if (requiredItemId && tokenId !== requiredItemId) continue;
            
            // Check URN requirements
            const urnRequirement = deal.token_gate_urn_requirements && deal.token_gate_urn_requirements[index];
            const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
            
            if (requiredUrnItemId) {
              // For lookup tool, we'll let the backend handle URN validation with dynamic detection
              // Skip the frontend URN check since we might not have the collectionItem for blockchain token IDs
              console.log('🔍 Deal has URN requirement, will validate via backend:', requiredUrnItemId);
            }
            
            qualifies = true;
            break;
          }
        }

        if (qualifies) {
          // For deals, check purchase limits similar to recipes
          const dealRewards = [];
          
          // Extract deal images/rewards
          if (deal.deal_items && deal.deal_items.length > 0) {
            // Multi-item deal
            deal.deal_items.forEach(item => {
              if (item.deal_image) {
                dealRewards.push({
                  image: item.deal_image,
                  name: item.deal_name || deal.deal_name || 'Deal Item',
                  quantity: 1
                });
              }
            });
          } else if (deal.deal_image) {
            // Single item deal
            dealRewards.push({
              image: deal.deal_image,
              name: deal.deal_name || deal.title || deal.name || 'Deal Item',
              quantity: 1
            });
          }
          
          // For lookup tool, just check token usage without ownership validation
          console.log('🔍 Deal check:', deal.id, 'limit_per_token_enabled:', deal.limit_per_token_enabled, 'limit_per_token:', deal.limit_per_token);
          
          if (deal.limit_per_token_enabled && deal.limit_per_token) {
            // Check token usage for the specific NFT being looked up
            try {
              console.log('🔍 Checking token usage for deal:', deal.id, 'NFT:', collection, tokenId);
              
              const tokenUsageResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=check-token-usage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  dealId: deal.id,
                  collection: collection.toLowerCase(),
                  tokenId: parseInt(tokenId)
                })
              });

              console.log('🔍 Token usage response status:', tokenUsageResponse.status);

              if (tokenUsageResponse.ok) {
                const tokenUsageData = await tokenUsageResponse.json();
                console.log('🔍 Token usage response data:', tokenUsageData);
                
                // If wallet is connected, also check wallet limits
                let walletUsed = null;
                let walletRemaining = null;
                
                if (wallet && deal.limit_per_wallet) {
                  try {
                    const walletLimitResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=check-purchase-eligibility`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        dealId: deal.id,
                        walletAddress: wallet,
                        quantity: 1,
                        ownedNFTs: [{ 
                          collection: collection.toLowerCase(), 
                          tokenId: parseInt(tokenId), 
                          title: 'Lookup NFT',
                          name: 'Lookup NFT'
                        }]
                      })
                    });
                    
                    if (walletLimitResponse.ok) {
                      const walletData = await walletLimitResponse.json();
                      walletUsed = walletData.walletUsed || 0;
                      walletRemaining = deal.limit_per_wallet - walletUsed;
                    } else {
                      // If wallet check fails (403, etc.), still show the limits without usage
                      walletUsed = 0; // Default to 0 if we can't check
                      walletRemaining = deal.limit_per_wallet;
                    }
                  } catch (error) {
                    console.log('⚠️ Wallet limit check failed:', error.message);
                    walletUsed = 0;
                    walletRemaining = deal.limit_per_wallet;
                  }
                }
                
                results.dealsAvailable.push({
                  title: deal.deal_name || deal.title || deal.name || `Deal #${deal.id}`,
                  description: deal.description,
                  price: deal.price,
                  id: deal.id,
                  rewards: dealRewards,
                  dealType: deal.deal_type || 'phygital',
                  hasDiscountAccess: false,
                  // Show token-specific usage data
                  tokenLimit: deal.limit_per_token,
                  tokenUsed: tokenUsageData.tokenUsed || 0,
                  tokenRemaining: tokenUsageData.tokenRemaining !== null ? tokenUsageData.tokenRemaining : (deal.limit_per_token - (tokenUsageData.tokenUsed || 0)),
                  // Show wallet limits if user has a wallet connected
                  walletLimit: wallet && deal.limit_per_wallet ? deal.limit_per_wallet : null,
                  walletUsed: walletUsed,
                  walletRemaining: walletRemaining
                });
              } else {
                const errorData = await tokenUsageResponse.json().catch(() => ({ error: 'Unknown error' }));
                console.error('🔍 Token usage check failed:', tokenUsageResponse.status, errorData);
                
                // Fallback if token usage check fails
                results.dealsAvailable.push({
                  title: deal.deal_name || deal.title || deal.name || `Deal #${deal.id}`,
                  description: deal.description,
                  price: deal.price,
                  id: deal.id,
                  rewards: dealRewards,
                  dealType: deal.deal_type || 'phygital',
                  hasDiscountAccess: false,
                  tokenLimit: deal.limit_per_token,
                  tokenUsed: 0,
                  tokenRemaining: deal.limit_per_token,
                  walletLimit: wallet && deal.limit_per_wallet ? deal.limit_per_wallet : null,
                  accessNote: `Unable to check usage data (${tokenUsageResponse.status})`
                });
              }
            } catch (error) {
              console.error('Error checking token usage:', error);
              // Fallback if request fails
              results.dealsAvailable.push({
                title: deal.deal_name || deal.title || deal.name || `Deal #${deal.id}`,
                description: deal.description,
                price: deal.price,
                id: deal.id,
                rewards: dealRewards,
                dealType: deal.deal_type || 'phygital',
                hasDiscountAccess: false,
                tokenLimit: deal.limit_per_token,
                tokenUsed: 0,
                tokenRemaining: deal.limit_per_token,
                walletLimit: wallet && deal.limit_per_wallet ? deal.limit_per_wallet : null,
                accessNote: 'Connection error - unable to check usage data'
              });
            }
          } else {
            // No per-token limits, just add the deal with wallet limits if available
            console.log('🔍 Deal has no per-token limits:', deal.id, 'enabled:', deal.limit_per_token_enabled, 'limit:', deal.limit_per_token);
            
            let walletUsed = null;
            let walletRemaining = null;
            
            // If wallet is connected and deal has wallet limits, check them
            if (wallet && deal.limit_per_wallet) {
              try {
                const walletLimitResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=check-purchase-eligibility`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    dealId: deal.id,
                    walletAddress: wallet,
                    quantity: 1,
                    ownedNFTs: [{ 
                      collection: collection.toLowerCase(), 
                      tokenId: parseInt(tokenId), 
                      title: 'Lookup NFT',
                      name: 'Lookup NFT'
                    }]
                  })
                });
                
                if (walletLimitResponse.ok) {
                  const walletData = await walletLimitResponse.json();
                  walletUsed = walletData.walletUsed || 0;
                  walletRemaining = deal.limit_per_wallet - walletUsed;
                } else {
                  walletUsed = 0;
                  walletRemaining = deal.limit_per_wallet;
                }
              } catch (error) {
                console.log('⚠️ Wallet limit check failed:', error.message);
                walletUsed = 0;
                walletRemaining = deal.limit_per_wallet;
              }
            }
            
            results.dealsAvailable.push({
              title: deal.deal_name || deal.title || deal.name || `Deal #${deal.id}`,
              description: deal.description,
              price: deal.price,
              id: deal.id,
              rewards: dealRewards,
              dealType: deal.deal_type || 'phygital',
              hasDiscountAccess: false,
              walletLimit: wallet && deal.limit_per_wallet ? deal.limit_per_wallet : null,
              walletUsed: walletUsed,
              walletRemaining: walletRemaining
            });
          }
        }
      }

      // Check faucet access
      if (faucetConfig && faucetConfig.enabled && faucetConfig.token_gate_requirements && faucetConfig.token_gate_requirements.length > 0) {
        let qualifiesForFaucet = false;
        
        // Check if this NFT qualifies for faucet access
        for (const gate of faucetConfig.token_gate_requirements) {
          const requiredCollection = gate.collection.toLowerCase();
          if (collection.toLowerCase() !== requiredCollection) continue;
          
          const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
          if (requiredItemId) {
            // Check if this token matches the required URN item ID
            if (!collectionItem || collectionItem.urn_item_id !== requiredItemId) {
              continue;
            }
          }
          
          qualifiesForFaucet = true;
          break;
        }

        if (qualifiesForFaucet) {
          // Calculate frequency display
          const frequencyHours = faucetConfig.frequency_hours;
          let frequencyText = '';
          if (frequencyHours >= 24) {
            const days = Math.floor(frequencyHours / 24);
            const remainingHours = frequencyHours % 24;
            if (remainingHours === 0) {
              frequencyText = `Every ${days} day${days > 1 ? 's' : ''}`;
            } else {
              frequencyText = `Every ${days} day${days > 1 ? 's' : ''} and ${remainingHours} hour${remainingHours > 1 ? 's' : ''}`;
            }
          } else {
            frequencyText = `Every ${frequencyHours} hour${frequencyHours > 1 ? 's' : ''}`;
          }

          results.faucetAvailable = {
            title: 'POL Faucet',
            amount: faucetConfig.amount_pol,
            frequency: frequencyText,
            frequencyHours: faucetConfig.frequency_hours,
            canClaim: faucetStatus?.canClaim || false,
            reason: faucetStatus?.reason || '',
            nextClaimTime: faucetStatus?.nextClaimTime || null
          };
        }
      }

      setLookupResults(results);
    } catch (error) {
      console.error('Error checking NFT eligibility:', error);
      onError({
        title: 'Lookup Failed',
        message: 'Failed to check claim eligibility. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = () => {
    const parsed = parseMarketplaceUrl(lookupUrl);
    if (!parsed) {
      onError({
        title: 'Invalid URL',
        message: 'Please enter a valid Decentraland or OpenSea marketplace URL.',
        type: 'error'
      });
      return;
    }

    checkNFTEligibility(parsed.collection, parsed.tokenId);
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>🔍 Claim Lookup Tool</h3>
        <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Paste a Decentraland or OpenSea marketplace link to check how many claims remain for that NFT.
        </p>
        
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            type="text"
            value={lookupUrl}
            onChange={(e) => setLookupUrl(e.target.value)}
            placeholder="https://opensea.io/item/polygon/0x.../123 or https://market.decentraland.org/contracts/0x.../tokens/123"
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '8px',
              border: '1px solid #444',
              background: '#2a2a2a',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />
          <button
            onClick={handleLookup}
            disabled={!lookupUrl || isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              background: lookupUrl && !isLoading ? 'linear-gradient(45deg, #007bff, #0056b3)' : '#666',
              color: 'white',
              fontWeight: 'bold',
              cursor: lookupUrl && !isLoading ? 'pointer' : 'not-allowed'
            }}
          >
            {isLoading ? 'Checking...' : 'LOOKUP'}
          </button>
        </div>
      </div>

      {lookupResults && (
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: '12px', 
          padding: '1.5rem',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {lookupResults.error ? (
            // Show error message for invalid/non-existent NFTs
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#ff6b6b'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                NFT Not Found
              </div>
              <div style={{ fontSize: '1rem', color: '#aaa', marginBottom: '1rem' }}>
                {lookupResults.error}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                Collection: {lookupResults.nft.collection.slice(0, 6)}...{lookupResults.nft.collection.slice(-4)}
                <br />
                Token ID: {lookupResults.nft.tokenId}
              </div>
            </div>
          ) : (
            // Show normal lookup results
            <>
              <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ color: '#fff', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              Lookup Results
            </h4>
            <div style={{ color: '#aaa', fontSize: '0.85rem' }}>
              Collection: {lookupResults.nft.collection.slice(0, 6)}...{lookupResults.nft.collection.slice(-4)}
            </div>
            <div style={{ color: '#aaa', fontSize: '0.85rem' }}>
              Token ID: {lookupResults.nft.tokenId}
            </div>
            
            {/* Show NFT info if it's a DOCTORdripp collectible */}
            {lookupResults.isDOCTORdrippCollectible && lookupResults.nftInfo && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(0, 255, 136, 0.1)', 
                borderRadius: '8px', 
                border: '1px solid rgba(0, 255, 136, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                {/* NFT Image */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: '60px'
                }}>
                  {lookupResults.nftInfo?.image ? (
                    <img
                      src={lookupResults.nftInfo.image}
                      alt={lookupResults.nftInfo.title || 'NFT'}
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'contain',
                        borderRadius: '6px',
                        border: '2px solid rgba(0, 255, 136, 0.5)',
                        background: 'rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'rgba(0, 255, 136, 0.2)',
                      borderRadius: '6px',
                      border: '2px solid rgba(0, 255, 136, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      color: '#00ff88'
                    }}>
                      🖼️
                    </div>
                  )}
                </div>

                {/* NFT Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '1.3rem', marginBottom: '0.25rem' }}>
                    ✅ DOCTORdripp Collectible: {lookupResults.nftInfo.title}
                  </div>
                  {lookupResults.nftInfo.urnItemId && (
                    <div style={{ color: '#aaa', fontSize: '0.85rem' }}>
                      URN Item ID: {lookupResults.nftInfo.urnItemId}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Show if it's not a DOCTORdripp collectible */}
            {!lookupResults.isDOCTORdrippCollectible && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '6px', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                <div style={{ color: '#ffc107', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  ⚠️ Not a DOCTORdripp Collectible
                </div>
              </div>
            )}
          </div>

          {/* Show available claims summary */}
          {lookupResults.totalAvailable > 0 && (
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#00ff88',
              textAlign: 'center',
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'rgba(0, 255, 136, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(0, 255, 136, 0.3)'
            }}>
              {lookupResults.totalAvailable} Claims Available!
            </div>
          )}

          {/* Show all qualifying free mint recipes */}
          {lookupResults.freeMintsAvailable.length > 0 && (
            <>
              <h5 style={{ color: '#fff', marginBottom: '1rem', marginTop: '1.5rem' }}>Free Mint Claims</h5>
              {lookupResults.freeMintsAvailable.map((freeMint, index) => (
                <div key={index} style={{
                  background: freeMint.remaining > 0 
                    ? 'rgba(0, 123, 255, 0.1)' 
                    : 'rgba(255, 107, 107, 0.1)',
                  border: freeMint.remaining > 0 
                    ? '1px solid rgba(0, 123, 255, 0.3)' 
                    : '1px solid rgba(255, 107, 107, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    {/* Reward Images Column */}
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '100px'
                    }}>
                      {freeMint.rewards && freeMint.rewards.length > 0 ? (
                        <div style={{ 
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {freeMint.rewards.map((reward, rewardIndex) => (
                            <div key={rewardIndex} style={{
                              position: 'relative',
                              zIndex: freeMint.rewards.length - rewardIndex
                            }}>
                              {reward.image && (
                                <img
                                  src={reward.image}
                                  alt={reward.name || 'Reward'}
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'contain',
                                    borderRadius: '6px',
                                    border: '2px solid #444',
                                    background: 'rgba(0, 0, 0, 0.3)'
                                  }}
                                />
                              )}
                              {freeMint.rewards.length > 1 && rewardIndex < freeMint.rewards.length - 1 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: '-15px',
                                  transform: 'translateY(-50%)',
                                  background: '#8a2be2',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid #1a1a1a',
                                  zIndex: 1000
                                }}>
                                  OR
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          border: '2px dashed #666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          color: '#666'
                        }}>
                          ?
                        </div>
                      )}
                    </div>

                    {/* Text Content Column */}
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ 
                        color: freeMint.remaining > 0 ? '#007bff' : '#ff6b6b', 
                        fontWeight: 'bold', 
                        fontSize: '1.3rem',
                        marginBottom: '0.5rem' 
                      }}>
                        {freeMint.title}
                      </div>
                      
                      {/* Type Badge */}
                      <div style={{
                        display: 'inline-block',
                        backgroundColor: '#007bff',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        marginBottom: '0.75rem'
                      }}>
                        {getDisplayType(freeMint, true)}
                      </div>
                      
                      {freeMint.error ? (
                        <div style={{ color: '#ffc107', fontSize: '0.9rem' }}>
                          ⚠️ {freeMint.error}
                        </div>
                      ) : (
                        <>
                          {/* Primary info: This NFT's usage */}
                          {freeMint.tokenRemaining !== null && (
                            <div style={{ 
                              backgroundColor: 'rgba(0, 255, 136, 0.15)',
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                              🎯 This NFT: {freeMint.tokenUsed !== undefined ? `${freeMint.tokenUsed}/${freeMint.tokenLimit} uses` : `${freeMint.tokenLimit} max uses`}
                              {freeMint.tokenRemaining !== null && (
                                <div style={{ fontSize: '0.9rem', color: '#aaa', marginTop: '0.25rem' }}>
                                  {freeMint.tokenRemaining > 0 ? `${freeMint.tokenRemaining} use${freeMint.tokenRemaining === 1 ? '' : 's'} remaining` : 'No uses remaining'}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Secondary info: User's wallet limits */}
                          {freeMint.walletLimit && (
                            <div style={{ 
                              fontSize: '0.9rem', 
                              color: '#bbb', 
                              fontStyle: 'italic',
                              marginBottom: '0.5rem'
                            }}>
                              👤 Your wallet limit: {freeMint.walletUsed !== undefined ? `${freeMint.walletUsed}/${freeMint.walletLimit} used` : `${freeMint.walletLimit} max`} ({freeMint.walletUsed !== undefined && freeMint.walletLimit ? Math.max(0, freeMint.walletLimit - freeMint.walletUsed) : freeMint.walletLimit} remaining)
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Show all qualifying deals */}
          {lookupResults.dealsAvailable.length > 0 && (
            <>
              <h5 style={{ color: '#fff', marginBottom: '1rem', marginTop: '1.5rem' }}>Token-Gated Deals</h5>
              {lookupResults.dealsAvailable.map((deal, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    {/* Reward Images Column */}
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '100px'
                    }}>
                      {deal.rewards && deal.rewards.length > 0 ? (
                        <div style={{ 
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {deal.rewards.map((reward, rewardIndex) => (
                            <div key={rewardIndex} style={{
                              position: 'relative',
                              zIndex: deal.rewards.length - rewardIndex
                            }}>
                              {reward.image && (
                                <img
                                  src={reward.image}
                                  alt={reward.name || 'Deal Item'}
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'contain',
                                    borderRadius: '6px',
                                    border: '2px solid #444',
                                    background: 'rgba(0, 0, 0, 0.3)'
                                  }}
                                />
                              )}
                              {deal.rewards.length > 1 && rewardIndex < deal.rewards.length - 1 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: '-15px',
                                  transform: 'translateY(-50%)',
                                  background: '#8a2be2',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid #1a1a1a',
                                  zIndex: 1000
                                }}>
                                  OR
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          border: '2px dashed #666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          color: '#666'
                        }}>
                          $
                        </div>
                      )}
                    </div>

                    {/* Text Content Column */}
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ 
                        color: '#ffc107', 
                        fontWeight: 'bold', 
                        fontSize: '1.3rem',
                        marginBottom: '0.5rem' 
                      }}>
                        {deal.title || 'Untitled Deal'}
                      </div>
                      
                      {/* Type Badge */}
                      <div style={{
                        display: 'inline-block',
                        backgroundColor: deal.dealType === 'dcl_wearable' ? '#8a2be2' : 
                                       deal.hasDiscountAccess ? '#28a745' : '#ff8c00',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        marginBottom: '0.75rem'
                      }}>
                        {getDisplayType(deal, false)}
                      </div>
                      
                      {deal.error ? (
                        <div style={{ color: '#ffc107', fontSize: '0.9rem' }}>
                          ⚠️ {deal.error}
                        </div>
                      ) : deal.accessNote ? (
                        <>
                          <div style={{ color: '#ffc107', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                            ⚠️ {deal.accessNote}
                          </div>
                          {/* Show purchase limits even without access */}
                          {(deal.walletLimit || deal.tokenLimit) && (
                            <>
                              {/* Primary info: Token-specific limits for the NFT being looked up */}
                              {deal.tokenLimit && (
                                <div style={{ 
                                  color: '#fff', 
                                  fontSize: '1.2rem', 
                                  fontWeight: 'bold',
                                  marginBottom: '0.5rem',
                                  padding: '0.5rem',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                  🎯 This NFT: {deal.tokenUsed !== undefined ? `${deal.tokenUsed}/${deal.tokenLimit} uses` : `${deal.tokenLimit} max uses`}
                                  {deal.tokenRemaining !== null && (
                                    <div style={{ fontSize: '0.9rem', color: '#aaa', marginTop: '0.25rem' }}>
                                      {deal.tokenRemaining > 0 ? `${deal.tokenRemaining} use${deal.tokenRemaining === 1 ? '' : 's'} remaining` : 'No uses remaining'}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Secondary info: User's wallet limits */}
                              {deal.walletLimit && (
                                <div style={{ 
                                  color: '#aaa', 
                                  fontSize: '0.9rem',
                                  marginBottom: '0.5rem',
                                  fontStyle: 'italic'
                                }}>
                                  👤 Your wallet limit: {deal.walletUsed !== undefined ? `${deal.walletUsed}/${deal.walletLimit} used` : `${deal.walletLimit} max`}
                                  {deal.walletRemaining !== null && ` (${deal.walletRemaining} remaining)`}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : deal.limitInfoNote ? (
                        <div style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          ℹ️ {deal.limitInfoNote}
                        </div>
                      ) : (
                        <>
                          {/* Show purchase limits if available */}
                          {(deal.walletLimit || deal.tokenLimit) && (
                            <>
                              {/* Primary info: Token-specific limits for the NFT being looked up */}
                              {deal.tokenLimit && (
                                <div style={{ 
                                  color: '#fff', 
                                  fontSize: '1.2rem', 
                                  fontWeight: 'bold',
                                  marginBottom: '0.5rem',
                                  padding: '0.5rem',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                  🎯 This NFT: {deal.tokenUsed !== undefined ? `${deal.tokenUsed}/${deal.tokenLimit} uses` : `${deal.tokenLimit} max uses`}
                                  {deal.tokenRemaining !== null && (
                                    <div style={{ fontSize: '0.9rem', color: '#aaa', marginTop: '0.25rem' }}>
                                      {deal.tokenRemaining > 0 ? `${deal.tokenRemaining} use${deal.tokenRemaining === 1 ? '' : 's'} remaining` : 'No uses remaining'}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Secondary info: User's wallet limits */}
                              {deal.walletLimit && (
                                <div style={{ 
                                  color: '#aaa', 
                                  fontSize: '0.9rem',
                                  marginBottom: '0.5rem',
                                  fontStyle: 'italic'
                                }}>
                                  👤 Your wallet limit: {deal.walletUsed !== undefined ? `${deal.walletUsed}/${deal.walletLimit} used` : `${deal.walletLimit} max`}
                                  {deal.walletRemaining !== null && ` (${deal.walletRemaining} remaining)`}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      
                      {deal.description && (
                        <div style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          {deal.description}
                        </div>
                      )}
                      {deal.price && (
                        <div style={{ color: '#fff', fontWeight: 'bold' }}>
                          Price: ${deal.price}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Show faucet access */}
          {lookupResults.faucetAvailable && (
            <>
              <h5 style={{ color: '#fff', marginBottom: '1rem', marginTop: '1.5rem' }}>POL Faucet Access</h5>
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '0.5rem'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem'
                }}>
                  {/* Faucet Icon */}
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '100px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      💧
                    </div>
                  </div>

                  {/* Faucet Content */}
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ 
                      color: '#22c55e', 
                      fontWeight: 'bold', 
                      fontSize: '1.3rem',
                      marginBottom: '0.5rem' 
                    }}>
                      {lookupResults.faucetAvailable.title}
                    </div>
                    
                    {/* Type Badge */}
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: '#22c55e',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      marginBottom: '0.75rem'
                    }}>
                      POL FAUCET
                    </div>
                    
                    <div style={{ 
                      color: '#fff', 
                      fontSize: '1.1rem', 
                      fontWeight: 'bold',
                      marginBottom: '0.25rem'
                    }}>
                      {lookupResults.faucetAvailable.amount} POL per claim
                    </div>
                    
                    <div style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                      {lookupResults.faucetAvailable.frequency}
                    </div>
                    
                    {lookupResults.faucetAvailable.canClaim ? (
                      <div style={{ color: '#22c55e', fontSize: '0.9rem', fontWeight: 'bold' }}>
                        ✅ Ready to claim
                      </div>
                    ) : lookupResults.faucetAvailable.reason && (
                      <div style={{ color: '#ffc107', fontSize: '0.9rem' }}>
                        ⏳ {lookupResults.faucetAvailable.reason}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          </>
          )}

          {/* Show when no qualifying recipes/deals found */}
          {!lookupResults.error && lookupResults.freeMintsAvailable.length === 0 && lookupResults.dealsAvailable.length === 0 && !lookupResults.faucetAvailable && (
            <div style={{ 
              fontSize: '1.2rem', 
              color: '#ff6b6b',
              textAlign: 'center',
              padding: '2rem',
              background: 'rgba(255, 107, 107, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 107, 107, 0.3)'
            }}>
              No active recipes or deals for this NFT
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});


// build a "rarities" summary object from your backend-provided rarities array
function buildRarities(tokens) {
  const rarities = {};
  tokens.forEach(n => {
    // your backend attaches .rarities to each NFT
    (n.rarities || []).forEach(({ trait_type, value }) => {
      const key = (trait_type || 'unknown').trim();
      rarities[key] = rarities[key] || {};
      rarities[key][value] = (rarities[key][value] || 0) + 1;
    });
  });
  return rarities;
}

/**
 * For each NFT, find the one attribute whose trait_type contains "rarity"
 * and return exactly [{ tokenId, rarity }, ...].
 */
function flattenRarities(tokens) {
  return tokens
    .map(n => {
      const attrs = n.rarities || [];
      const rarityAttr = attrs.find(a => /rarity/i.test(a.trait_type || ''));
      if (!rarityAttr) return null;
      return {
        tokenId: n.tokenId.toString(),
        rarity: String(rarityAttr.value)
      };
    })
    .filter(Boolean);
}



// instead of the object-map version, use:
function computeRarityScore(raritiesArray) {
  const RARITY_SCORES = {
    unique: 500,
    mythic: 100,
    exotic: 50,
    legendary: 20,
    epic: 10,
    rare: 5,
    uncommon: 2,
    common: 1,
  };
  return raritiesArray.reduce((sum, { rarity }) => {
    if (typeof rarity === 'string') {
      return sum + (RARITY_SCORES[rarity.toLowerCase()] || 0);
    }
    if (typeof rarity === 'number') {
      return sum + rarity;
    }
    return sum;
  }, 0);
}

// Helper function to get collection item info by collection address and item ID
function getCollectionItemInfo(collectionItems, collectionAddress, itemId) {
  if (!itemId) {
    // If no item_id specified, return collection info
    const collection = collectionItems.find(
      item => item.collection_address.toLowerCase() === collectionAddress?.toLowerCase()
    );
    return {
      name: collection?.title || `${collectionAddress?.slice(0, 6)}...${collectionAddress?.slice(-4)}`,
      image: collection?.image || null
    };
  }
  
  // Find specific item by collection address and item_id
  // Use string comparison to handle type mismatches
  const collectionItem = collectionItems.find(
    item => item.collection_address.toLowerCase() === collectionAddress?.toLowerCase() && 
            (item.urn_item_id?.toString() === itemId?.toString() || item.token_id?.toString() === itemId?.toString())
  );
  
  return {
    name: collectionItem?.title || `Item #${itemId}`,
    image: collectionItem?.image || null
  };
}

/**
 * Client-side URN detection for user's NFTs
 * Matches NFTs against existing collection items using title patterns and token ID sequences
 * SECURITY: Only works for collections that already exist in collection_items (pre-approved)
 */
function detectUrnItemId(nft, collectionItems) {
  const collectionAddress = nft.collection.toLowerCase();
  const tokenId = nft.tokenId.toString();
  const nftTitle = (nft.title || nft.name || '').toLowerCase();
  
  // SECURITY CHECK: Only process collections that are already in collection_items
  const collectionItemsForCollection = collectionItems.filter(
    item => item.collection_address.toLowerCase() === collectionAddress
  );
  
  if (collectionItemsForCollection.length === 0) {
    // Collection not pre-approved - reject immediately
    console.log(`🚫 Collection ${collectionAddress} not in approved collection_items - rejecting URN detection`);
    return null;
  }
  
  // Method 1: Direct token ID match (exact match)
  const exactMatch = collectionItemsForCollection.find(
    item => item.token_id === tokenId
  );
  if (exactMatch) {
    return exactMatch.urn_item_id;
  }
  
  // Method 2: Title pattern matching (only within approved collection)
  if (nftTitle) {
    const titleWords = nftTitle
      .replace(/[^\w\s]/g, ' ') // Remove punctuation
      .split(/\s+/)
      .filter(word => word.length > 2); // Filter short words
    
    let bestMatch = null;
    let bestScore = 0;
    
    for (const item of collectionItemsForCollection) {
      if (!item.title || !item.urn_item_id) continue;
      
      const itemTitle = item.title.toLowerCase();
      const itemWords = itemTitle
        .replace(/[^\w\s]/g, ' ')
        .split(/\s+/)
        .filter(word => word.length > 2);
      
      // Calculate matching score
      let score = 0;
      for (const word of titleWords) {
        if (itemWords.includes(word)) {
          score++;
        }
      }
      
      // Bonus for exact title match
      if (nftTitle === itemTitle) {
        score += 10;
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = item;
      }
    }
    
    if (bestMatch && bestScore > 0) {
      return bestMatch.urn_item_id;
    }
  }
  
  // Method 3: Token ID sequence analysis (only within approved collection)
  // Check if the token ID is in sequence with existing items
  const tokenIdBigInt = BigInt(tokenId);
  
  for (const item of collectionItemsForCollection) {
    if (!item.token_id || !item.urn_item_id) continue;
    
    try {
      const itemTokenIdBigInt = BigInt(item.token_id);
      const difference = tokenIdBigInt - itemTokenIdBigInt;
      
      // If tokens are very close in sequence (within reasonable range)
      // This handles cases like your example: 537 vs 538
      if (difference >= 0n && difference <= 1000n) {
        console.log(`🔢 URN detected by sequence: token ${tokenId} is ${difference} after ${item.token_id} (URN ${item.urn_item_id}) in approved collection ${collectionAddress}`);
        return item.urn_item_id;
      }
    } catch (error) {
      // Skip if token ID is not a valid number
      continue;
    }
  }
  
  console.log(`🚫 Could not detect URN for ${collectionAddress}#${tokenId} (${nftTitle}) - no matches in approved collection`);
  return null;
}

function NFTDashboard({ wallet, onConnectWallet, onDisconnectWallet, isAuthenticating, showSecurityAlert, onDismissSecurityAlert, selectedWalletType, disconnectInstructions, isAdmin }) {
  
  // Get claim statistics for a recipe
  const getClaimStatistics = async (recipe, ownedNFTs) => {
    try {
      // Extract qualifying token IDs (same logic as checkTokenAvailability)
      const qualifyingTokenIds = [];
      
      if (recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0) {
        for (const [index, gate] of recipe.token_gate_requirements.entries()) {
          const requiredCollection = gate.collection.toLowerCase();
          const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
          
          // Get corresponding URN requirement if it exists
          const urnRequirement = recipe.token_gate_urn_requirements && recipe.token_gate_urn_requirements[index];
          const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
          
          const matchingNFTs = ownedNFTs.filter(nft => {
            if (nft.collection.toLowerCase() !== requiredCollection) return false;
            if (requiredItemId && nft.tokenId.toString() !== requiredItemId) return false;
            
            // If URN item ID is specified, check against URN item ID from collection_items
            if (requiredUrnItemId) {
              // First try to find exact match in collection_items
              let collectionItem = collectionItems.find(
                item => item.collection_address.toLowerCase() === nft.collection.toLowerCase() && 
                        item.token_id === nft.tokenId.toString()
              );
              
              // If no exact match, try client-side URN detection
              if (!collectionItem) {
                const detectedUrn = detectUrnItemId(nft, collectionItems);
                if (detectedUrn) {
                  collectionItem = { urn_item_id: detectedUrn };
                }
                }
                
                if (!collectionItem || collectionItem.urn_item_id.toString() !== requiredUrnItemId) {
                  return false;
                }
            }
            
            return true;
          });
          
          qualifyingTokenIds.push(...matchingNFTs.map(nft => nft.tokenId.toString()));
        }
      }

      // Call backend to get detailed statistics
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipe-validation?action=get-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipeId: recipe.id,
          walletAddress: wallet,
          qualifyingTokenIds: qualifyingTokenIds
        })
      });

      if (!response.ok) {
        return {
          walletUsed: 0,
          walletLimit: recipe.limit_per_wallet || 0,
          tokenUsed: 0,
          tokenLimit: recipe.limit_per_token || 1,
          perTokenEnabled: recipe.limit_per_token_enabled || false
        };
      }
      
      const data = await response.json();
      
      const result = {
        walletUsed: data.walletUsed || 0,
        walletLimit: data.walletLimit || recipe.limit_per_wallet || 0,
        totalTokenUsed: data.totalTokenUsed || 0,
        totalTokenLimit: data.totalTokenLimit || 0,
        tokenCount: data.tokenCount || 0,
        perTokenLimit: data.perTokenLimit || 1,
        perTokenEnabled: data.perTokenEnabled || false
      };
      
      return result;
    } catch (error) {
      console.error('Error getting claim statistics:', error);
      return {
        walletUsed: 0,
        walletLimit: recipe.limit_per_wallet || 0,
        totalTokenUsed: 0,
        totalTokenLimit: 0,
        tokenCount: 0,
        perTokenLimit: recipe.limit_per_token || 1,
        perTokenEnabled: false
      };
    }
  };

  // Check if there are any available tokens for claiming (for button state)
  const checkTokenAvailability = async (recipe, ownedNFTs) => {
    // First check basic token gate access
    if (!checkTokenGateAccess(recipe.token_gate_requirements, recipe.token_gate_urn_requirements, recipe.token_gate_logic, ownedNFTs)) {
      return false;
    }

    // For free mint recipes, check if there are any available tokens
    if (recipe.recipe_type === 'free_mint') {
      try {
        // Extract qualifying token IDs (same logic as checkRecipeEligibility)
        const qualifyingTokenIds = [];
        
        if (recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0) {
          for (const [index, gate] of recipe.token_gate_requirements.entries()) {
            const requiredCollection = gate.collection.toLowerCase();
            const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
            
            // Get corresponding URN requirement if it exists
            const urnRequirement = recipe.token_gate_urn_requirements && recipe.token_gate_urn_requirements[index];
            const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
            
            const matchingNFTs = ownedNFTs.filter(nft => {
              if (nft.collection.toLowerCase() !== requiredCollection) return false;
              if (requiredItemId && nft.tokenId.toString() !== requiredItemId) return false;
              
              // If URN item ID is specified, check against URN item ID from collection_items
              if (requiredUrnItemId) {
                // First try to find exact match in collection_items
                let collectionItem = collectionItems.find(
                  item => item.collection_address.toLowerCase() === nft.collection.toLowerCase() && 
                          item.token_id === nft.tokenId.toString()
                );
                
                // If no exact match, try client-side URN detection
                if (!collectionItem) {
                  const detectedUrn = detectUrnItemId(nft, collectionItems);
                  if (detectedUrn) {
                    collectionItem = { urn_item_id: detectedUrn };
                  }
                }
                
                if (!collectionItem || collectionItem.urn_item_id.toString() !== requiredUrnItemId) {
                  return false;
                }
              }
              
              return true;
            });
            
            qualifyingTokenIds.push(...matchingNFTs.map(nft => nft.tokenId.toString()));
          }
        }

        // Call backend to check if any tokens are available
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipe-validation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipeId: recipe.id,
            walletAddress: wallet,
            recipeType: recipe.recipe_type,
            qualifyingTokenIds: qualifyingTokenIds
          })
        });

        if (!response.ok) return false;
        
        const data = await response.json();
        return data.canClaim;
      } catch (error) {
        console.error('Error checking token availability:', error);
        return false;
      }
    }
    
    return true; // For non-free-mint recipes, assume available if access is granted
  };

  // Check comprehensive recipe eligibility (token gates + limits)
  const checkRecipeEligibility = async (recipe, ownedNFTs) => {
  // First check basic token gate access
  if (!checkTokenGateAccess(recipe.token_gate_requirements, recipe.token_gate_urn_requirements, recipe.token_gate_logic, ownedNFTs)) {
    return {
      canClaim: false,
      reason: 'You need to own the required collections to access this recipe. Check the Access Requirements above to see what you need.',
      type: 'access'
    };
  }

  // For free mint and token-gated recipes, check backend limits
  if (recipe.recipe_type === 'free_mint' || recipe.recipe_type === 'token_gated') {
    try {
      // Extract qualifying token IDs
      let qualifyingTokenIds = [];
      if (recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0) {
        for (const [index, gate] of recipe.token_gate_requirements.entries()) {
          const requiredCollection = gate.collection.toLowerCase();
          const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
          
          // Get corresponding URN requirement if it exists
          const urnRequirement = recipe.token_gate_urn_requirements && recipe.token_gate_urn_requirements[index];
          const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
          
          const matchingNFTs = ownedNFTs.filter(nft => {
            if (nft.collection.toLowerCase() !== requiredCollection) return false;
            
            // If specific item_id is required (for locking to specific blockchain token ID), check that first
            if (requiredItemId && nft.tokenId.toString() !== requiredItemId) {
              return false;
            }
            
            // If URN item ID is specified, check against URN item ID from collection_items
            if (requiredUrnItemId) {
              // First try to find exact match in collection_items
              let collectionItem = collectionItems.find(
                item => item.collection_address.toLowerCase() === nft.collection.toLowerCase() && 
                        item.token_id === nft.tokenId.toString()
              );
              
              // If no exact match, try client-side URN detection
              if (!collectionItem) {
                const detectedUrn = detectUrnItemId(nft, collectionItems);
                if (detectedUrn) {
                  // Create a virtual collection item for this NFT
                  collectionItem = { urn_item_id: detectedUrn };
                }
              }
              
              if (!collectionItem) {
                return false;
              }
              
              // Check if the URN item ID matches the required URN item ID
              if (collectionItem.urn_item_id.toString() !== requiredUrnItemId) {
                return false;
              }
            }
            
            return true;
          });
          
          if (recipe.recipe_type === 'free_mint') {
            // For FREE MINT: Send all qualifying NFTs, backend will pick first unused one
            qualifyingTokenIds.push(...matchingNFTs.map(nft => nft.tokenId.toString()));
          } else {
            // For TOKEN-GATED: Would need selected NFTs (not implemented here)
            qualifyingTokenIds.push(...matchingNFTs.map(nft => nft.tokenId.toString()));
          }
        }
      }

      // Call backend to check limits
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/nft?action=check-recipe-limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipeId: recipe.id,
          walletAddress: wallet,
          qualifyingTokenIds: qualifyingTokenIds,
          recipeType: recipe.recipe_type
        })
      });

      const data = await response.json();
      
      if (!response.ok || !data.canClaim) {
        return {
          canClaim: false,
          reason: data.reason || 'You have reached the limit for this recipe.',
          type: 'limit'
        };
      }

      return { canClaim: true };
    } catch (error) {
      console.error('Error checking recipe eligibility:', error);
      return {
        canClaim: false,
        reason: 'Unable to verify eligibility. Please try again.',
        type: 'error'
      };
    }
  }

    return { canClaim: true };
  };

  // Check if user has access to token-gated recipe based on owned NFTs
  const checkTokenGateAccess = (tokenGateRequirements, tokenGateUrnRequirements, tokenGateLogic, ownedNFTs) => {
    if (!tokenGateRequirements || tokenGateRequirements.length === 0) {
      return true; // No token gates = open access
    }

    const gateResults = tokenGateRequirements.map((gate, index) => {
      const requiredCollection = gate.collection.toLowerCase();
      const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
      const requiredQuantity = gate.quantity || 1;
      
      // Get corresponding URN requirement if it exists
      const urnRequirement = tokenGateUrnRequirements && tokenGateUrnRequirements[index];
      const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;

      // Count matching NFTs in user's collection
      const matchingNFTs = ownedNFTs.filter(nft => {
        if (nft.collection.toLowerCase() !== requiredCollection) return false;
        
        // If specific item_id is required (for locking to specific blockchain token ID), check that first
        if (requiredItemId && nft.tokenId.toString() !== requiredItemId) {
          return false;
        }
        
        // If URN item ID is specified, check against URN item ID from collection_items
        if (requiredUrnItemId) {
          // First try to find exact match in collection_items
          let collectionItem = collectionItems.find(
            item => item.collection_address.toLowerCase() === nft.collection.toLowerCase() && 
                    item.token_id === nft.tokenId.toString()
          );
          
          // If no exact match, try client-side URN detection
          if (!collectionItem) {
            const detectedUrn = detectUrnItemId(nft, collectionItems);
            if (detectedUrn) {
              // Create a virtual collection item for this NFT
              collectionItem = { urn_item_id: detectedUrn };
            }
          }
          
          if (!collectionItem) {
            return false;
          }
          
          // Check if the URN item ID matches the required URN item ID
          // Ensure consistent type comparison by converting both to strings
          if (collectionItem.urn_item_id.toString() !== requiredUrnItemId) {
            return false;
          }
        }
        
        return true;
      });

      return matchingNFTs.length >= requiredQuantity;
    });

    // Apply logic: AND = all gates must pass, OR = any gate must pass
    return tokenGateLogic === 'OR'
      ? gateResults.some(Boolean)
      : gateResults.every(Boolean);
  };

  // Helper function to get the correct Ethereum provider based on connected wallet type
  const getEthereumProvider = () => {
    if (selectedWalletType === 'walletconnect') {
      // For WalletConnect, use the provider from walletManager
      console.log('🔗 Using WalletConnect provider from walletManager');
      const wcProvider = walletManager.walletConnectProvider;
      if (!wcProvider) {
        throw new Error('WalletConnect provider not found. Please reconnect your wallet.');
      }
      return wcProvider;
    }
    
    if (selectedWalletType === 'magiceden') {
      // Magic Eden only has .ethereum provider for EVM transactions (multiChain is for their custom API)
      console.log('Magic Eden providers available:', {
        ethereum: !!window.magicEden?.ethereum,
        polygon: !!window.magicEden?.polygon,
        bitcoin: !!window.magicEden?.bitcoin,
        multiChain: !!window.magicEden?.multiChain
      });
      
      console.log('📝 Note: multiChain is Magic Eden custom API, not EVM provider');
      const magicProvider = window.magicEden?.ethereum;
      if (!magicProvider) {
        throw new Error('Magic Eden Wallet provider not found. Please reconnect your wallet.');
      }
      return magicProvider;
    }
    
    // For Coinbase Wallet, use the provider from walletManager
    if (selectedWalletType === 'coinbase') {
      console.log('💼 Using Coinbase Wallet provider from walletManager');
      const cbProvider = walletManager.coinbaseProvider;
      if (!cbProvider) {
        throw new Error('Coinbase Wallet provider not found. Please reconnect your wallet. If you\'re on mobile, try using the WalletConnect option for better support.');
      }
      return cbProvider;
    }
    
    // For all other wallets (MetaMask, Rainbow, etc.)
    const provider = window.ethereum;
    if (!provider) {
      throw new Error('Wallet provider not found. Please reconnect your wallet or try using WalletConnect for mobile wallets.');
    }
    return provider;
  };

  // Helper function to get signer - uses JsonRpcSigner for WalletConnect (mobile-compatible)
  const getSignerForWallet = async (provider, chainId, address) => {
    if (selectedWalletType === 'walletconnect') {
      // For WalletConnect, use JsonRpcSigner pattern (per Reown docs) for mobile compatibility
      console.log('🔗 Using JsonRpcSigner for WalletConnect');
      return new JsonRpcSigner(provider, address);
    }
    // For other wallets, use standard getSigner
    return await provider.getSigner();
  };

  const [nfts, setNfts] = useState([]);
  const [raritiesMap, setRaritiesMap] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [selectedNFTs, setSelectedNFTs] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showLocked, setShowLocked] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalConfig, setErrorModalConfig] = useState({
    title: 'Error',
    message: 'An error occurred',
    type: 'error'
  });
  const [showDCLModal, setShowDCLModal] = useState(false);
  const [selectedDCLDeal, setSelectedDCLDeal] = useState(null);
  const [selectedDCLQuantity, setSelectedDCLQuantity] = useState(1);
  const [collectionItems, setCollectionItems] = useState([]);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [recipeToCraft, setRecipeToCraft] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [hasFetchedNfts, setHasFetchedNfts] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [tokenAvailability, setTokenAvailability] = useState({}); // Track token availability for each recipe
  // const [recipeAvailability, setRecipeAvailability] = useState({}); // Temporarily disabled
  // const [dealAvailability, setDealAvailability] = useState({}); // Temporarily disabled
  const [claimStatistics, setClaimStatistics] = useState({}); // Track claim statistics for each recipe
  
  // Check availability for recipes and deals on load
  // TEMPORARILY DISABLED - Enable after fixing Vercel build issue
  /*
  useEffect(() => {
    const checkAvailabilities = async () => {
      // Check recipe availability - only for free_mint recipes
      const freeMintRecipes = upgrades.filter(u => u.recipe_type === 'free_mint');
      
      if (freeMintRecipes.length === 0) return;
      
      // Batch check all recipes (limit to prevent too many calls)
      const recipesToCheck = freeMintRecipes.slice(0, 20); // Limit to first 20
      
      for (const recipe of recipesToCheck) {
        try {
          const { data, error } = await supabase.rpc('is_recipe_available', { p_recipe_id: recipe.id });
          if (!error && data && data.length > 0) {
            setRecipeAvailability(prev => ({
              ...prev,
              [recipe.id]: data[0].available !== false // Default to true if undefined
            }));
          } else {
            // If error or no data, assume available (fail open)
            setRecipeAvailability(prev => ({
              ...prev,
              [recipe.id]: true
            }));
          }
        } catch (err) {
          console.error(`Error checking availability for recipe ${recipe.id}:`, err);
          // Fail open - assume available on error
          setRecipeAvailability(prev => ({
            ...prev,
            [recipe.id]: true
          }));
        }
      }
    };
    
    if (upgrades.length > 0) {
      checkAvailabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgrades]);
  */
  const [sortMode, setSortMode] = useState('daily');
  const [justMinted, setJustMinted] = useState(0);
  const [initialUpgradedCount, setInitialUpgradedCount] = useState(0);
  const initialStatsPosted = useRef(false);
  const particleIntervalRef = useRef(null);
  
  // Background music state
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.3); // 30% default volume
  const audioRef = useRef(null);
  
  // Token balances state
  const [tokenBalances, setTokenBalances] = useState({
    polygonMatic: '...',
    polygonMana: '...',
    monadNative: '...',
    monadDrip: '...',
    genesisSkullsCount: '...'
  });
  const [balancesLoading, setBalancesLoading] = useState(false);
  
  // Mobile detection
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
  }, []);

  // Fetch token balances for all chains (without switching user's chain)
  const fetchTokenBalances = async (walletAddress) => {
    if (!walletAddress) return;
    
    setBalancesLoading(true);
    try {
      const balances = {
        polygonMatic: '0.00',
        polygonMana: '0.00',
        monadNative: 'N/A',
        monadDrip: 'N/A',
        genesisSkullsCount: '0'
      };

      // Fetch Polygon balances using public RPC
      const polygonRpcUrl = 'https://polygon-rpc.com';
      try {
        // Helper for direct RPC calls
        const polygonRpc = async (method, params) => {
          const response = await fetch(polygonRpcUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params })
          });
          const data = await response.json();
          if (data.error) throw new Error(data.error.message);
          return data.result;
        };

        // Get MATIC/POL balance
        const maticBalanceHex = await polygonRpc('eth_getBalance', [walletAddress, 'latest']);
        balances.polygonMatic = parseFloat(Number(BigInt(maticBalanceHex)) / 1e18).toFixed(2);

        // Get MANA balance using eth_call
        const balanceOfData = '0x70a08231000000000000000000000000' + walletAddress.slice(2).toLowerCase();
        const manaBalanceHex = await polygonRpc('eth_call', [
          { to: MANA_ADDRESS, data: balanceOfData },
          'latest'
        ]);
        balances.polygonMana = parseFloat(Number(BigInt(manaBalanceHex || '0x0')) / 1e18).toFixed(2);
      } catch (error) {
        console.error('Error fetching Polygon balances:', error);
      }

      // Fetch Monad balances using public RPC (keeps QuickNode API key secure on backend only)
      const monadRpcUrl = 'https://rpc2.monad.xyz';
      try {
        // Helper for RPC calls
        const monadRpc = async (method, params) => {
          const response = await fetch(monadRpcUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params })
          });
          const data = await response.json();
          if (data.error) throw new Error(data.error.message);
          return data.result;
        };

        // Get MON native balance
        const monBalanceHex = await monadRpc('eth_getBalance', [walletAddress, 'latest']);
        balances.monadNative = parseFloat(Number(BigInt(monBalanceHex)) / 1e18).toFixed(2);

        // Get DRIP token balance using eth_call
        const balanceOfData = '0x70a08231000000000000000000000000' + walletAddress.slice(2).toLowerCase();
        const dripBalanceHex = await monadRpc('eth_call', [
          { to: DRIP_ADDRESS, data: balanceOfData },
          'latest'
        ]);
        balances.monadDrip = parseFloat(Number(BigInt(dripBalanceHex || '0x0')) / 1e18).toFixed(2);
      } catch (error) {
        console.error('Error fetching Monad balances:', error);
        balances.monadNative = 'N/A';
        balances.monadDrip = 'N/A';
      }

      // Fetch Genesis Skulls count from database
      try {
        const nadscriptionsApiUrl = process.env.REACT_APP_API_URL 
          ? `${process.env.REACT_APP_API_URL}/api/nadscriptions`
          : 'https://api.doctordripp.com/api/nadscriptions';
        
        const response = await fetch(`${nadscriptionsApiUrl}?action=get-wallet-nadscriptions&walletAddress=${walletAddress}`);
        const data = await response.json();
        
        if (data.success && data.nadscriptions) {
          balances.genesisSkullsCount = data.nadscriptions.length.toString();
        }
      } catch (error) {
        console.error('Error fetching Genesis Skulls count:', error);
        balances.genesisSkullsCount = '0';
      }

      setTokenBalances(balances);
    } catch (error) {
      console.error('Error fetching token balances:', error);
    } finally {
      setBalancesLoading(false);
    }
  };

  // Helper function to show user-friendly error modals
  const showUserFriendlyError = (error, context = '') => {
    let title = 'Error';
    let message = 'An unexpected error occurred. Please try again.';
    let type = 'error';

    // Extract the actual error message from nested error objects
    let actualErrorMessage = error.message || '';
    
    // Check for nested error messages (common with Ledger and other hardware wallets)
    if (error.data?.cause?.message) {
      actualErrorMessage = error.data.cause.message;
    } else if (error.data?.message) {
      actualErrorMessage = error.data.message;
    } else if (error.cause?.message) {
      actualErrorMessage = error.cause.message;
    } else if (error.error?.message) {
      actualErrorMessage = error.error.message;
    }
    
    // If we still have the full error object as a string, try to extract just the message
    if (actualErrorMessage.includes('could not coalesce error') && actualErrorMessage.includes('"message":')) {
      try {
        const match = actualErrorMessage.match(/"message":\s*"([^"]+)"/);
        if (match && match[1]) {
          actualErrorMessage = match[1];
        }
      } catch (e) {
        // If parsing fails, keep the original message
      }
    }

    // Handle different error types
    if (error.response?.status === 429) {
      // Claim limit exceeded
      title = 'Claim Limit Exceeded';
      message = error.response.data?.reason || 'You have reached the claim limit for this recipe.';
      type = 'warning';
    } else if (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || actualErrorMessage?.includes('User denied') || actualErrorMessage?.includes('user denied') || actualErrorMessage?.includes('User rejected the request') || actualErrorMessage?.includes('User rejected the transaction') || actualErrorMessage?.includes('Ledger: User rejected') || actualErrorMessage?.includes('ethers-user-denied')) {
      // User cancelled transaction (supports MetaMask 4001, Rainbow -32603, and various message formats)
      title = 'Transaction Cancelled';
      message = 'You cancelled the transaction. No changes were made to your wallet or NFTs.';
      type = 'info';
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      title = 'Insufficient Funds';
      message = 'You don\'t have enough MATIC to complete this transaction. Please add more MATIC to your wallet.';
      type = 'warning';
    } else if (actualErrorMessage?.includes('network') || actualErrorMessage?.includes('connection')) {
      title = 'Network Error';
      message = 'There was a problem connecting to the blockchain. Please check your internet connection and try again.';
      type = 'warning';
    } else if (actualErrorMessage?.includes('signature')) {
      title = 'Signature Required';
      message = 'A signature is required to complete this action. Please sign the message in your wallet to proceed.';
      type = 'info';
    } else if (actualErrorMessage?.includes('EthAppNftNotSupported')) {
      // Ledger NFT operation not supported
      title = 'Ledger NFT Operation Not Supported';
      message = 'Your Ledger device doesn\'t support this operation through MetaMask. We recommend: 1) Disconnect your Ledger account from MetaMask, 2) Click "Connect Wallet" and choose "WalletConnect", 3) Select Ledger Live from the list. WalletConnect provides better Ledger support!';
      type = 'warning';
    } else if (actualErrorMessage?.includes('Ledger:') || actualErrorMessage?.includes('Ethereum app closed') || actualErrorMessage?.includes('Device is locked')) {
      // Ledger wallet errors (but not user rejection, which is handled above)
      if (!actualErrorMessage?.includes('User rejected')) {
        title = 'Ledger Wallet Issue';
        message = 'Your Ledger device needs attention. Please make sure your Ledger is unlocked and then open the Ethereum app on your Ledger device and try again.';
        type = 'warning';
      } else {
        // This is a Ledger user rejection, treat as cancellation
        title = 'Transaction Cancelled';
        message = 'You cancelled the transaction. No changes were made to your wallet or NFTs.';
        type = 'info';
      }
    } else if (context === 'craft' || context === 'recipe') {
      title = 'Recipe Crafting Failed';
      message = `Unable to craft this recipe. ${actualErrorMessage || 'Please check that you have the required NFTs and try again.'}`;
    } else if (context === 'upgrade') {
      title = 'Upgrade Failed';
      message = `Unable to upgrade your NFT. ${actualErrorMessage || 'Please try again or contact support if the problem persists.'}`;
    } else if (context === 'purchase') {
      title = 'Purchase Failed';
      message = `Unable to complete the purchase. ${actualErrorMessage || 'Please try again or contact support if the problem persists.'}`;
    } else {
      // Generic error
      message = actualErrorMessage || message;
    }

    setErrorModalConfig({ title, message, type });
    setShowErrorModal(true);
  };

  // State for slide-out panels
  const [showDealsPanel, setShowDealsPanel] = useState(false);
  const [showLeaderboardPanel, setShowLeaderboardPanel] = useState(false);
  const [showFaucetPanel, setShowFaucetPanel] = useState(false);
  const [showLookupPanel, setShowLookupPanel] = useState(false);
  const [showFaucetModal, setShowFaucetModal] = useState(false);
  const [faucetConfig, setFaucetConfig] = useState(null);
  const [faucetStatus, setFaucetStatus] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [maintenanceBypassed, setMaintenanceBypassed] = useState(false);
  
  // Mint Launching Mode state (null = unknown, false = disabled, true = enabled)
  const [mintLaunchingMode, setMintLaunchingMode] = useState(null);
  const [mintLaunchingConfig, setMintLaunchingConfig] = useState(null);

  const fetchLeaderboard = () =>
    api.get(`/api/stats?sortBy=${sortMode}`, {
      headers: {
        'x-api-access': 'leaderboard-access-2025'
      }
    })
      .then(({ data }) => setLeaderboard(data.leaderboard))
      .catch(console.error);

  const fetchFaucetConfig = async () => {
    try {
      const { data } = await api.get('/api/faucet', {
        params: { action: 'config' }
      });
      setFaucetConfig(data);
    } catch (err) {
      console.error('Error fetching faucet config:', err);
      setFaucetConfig(null);
    }
  };

  const fetchFaucetStatus = async () => {
    if (!wallet) return;
    try {
      const { data } = await api.get('/api/faucet', {
        params: { action: 'status', wallet }
      });
      setFaucetStatus(data);
    } catch (err) {
      console.error('Error fetching faucet status:', err);
      setFaucetStatus(null);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [sortMode]);

  useEffect(() => {
    fetchFaucetConfig();
  }, []);

  useEffect(() => {
    if (wallet) {
      fetchFaucetStatus();
    }
  }, [wallet]);

  // after you call fetchLeaderboard()…
  useEffect(() => {
    if (!wallet || !leaderboard.length) return;
    const me = leaderboard.find(u => u.wallet.toLowerCase() === wallet.toLowerCase());
    setInitialUpgradedCount(me?.upgraded_count || 0);
  }, [wallet, leaderboard]);

  // Initial check for maintenance and mint launching mode (runs once)
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const { data } = await api.get('/api/maintenance');
        setMaintenanceMode(data.enabled);
        setMaintenanceMessage(data.message);
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
        setMaintenanceMode(false);
      }
    };

    const checkMintLaunchingMode = async () => {
      try {
        const { data } = await api.get('/api/mint-launching');
        setMintLaunchingMode(data.enabled);
        setMintLaunchingConfig(data);
      } catch (error) {
        console.error('Error checking mint launching mode:', error);
        setMintLaunchingMode(false);
        setMintLaunchingConfig(null);
      }
    };

    // Only run initial checks
    checkMaintenanceMode();
    checkMintLaunchingMode();
  }, []); // Empty dependency array - runs only once

  // DISABLED: No more periodic checking - let the countdown component handle its own lifecycle
  // The MintLaunchingPage will call handleLaunchComplete when done

  // Handle launch completion callback from MintLaunchingPage
  const handleLaunchComplete = () => {
    console.log('🚀 Launch completed - disabling mint launching mode');
    setMintLaunchingMode(false);
    setMintLaunchingConfig(null);
  };

  // Helper function to get linked deals for a recipe from mint launching configuration
  const getLinkedDealsForRecipe = (recipeId) => {
    if (!mintLaunchingConfig || !mintLaunchingConfig.deals || !mintLaunchingConfig.recipe_to_deals_mapping) {
      return [];
    }
    
    const mapping = mintLaunchingConfig.recipe_to_deals_mapping || {};
    const dealIds = mapping[recipeId] || [];
    
    // Return deals that are linked to this recipe (including inactive ones)
    return mintLaunchingConfig.deals.filter(deal => dealIds.includes(deal.id));
  };

  // Helper function to resolve burn requirements with URN matching
  const resolveBurnRequirements = (requires, urnRequirements) => {
    if (!requires || !Array.isArray(requires)) return [];
    
    // URN matching is now handled in the backend, so just return the requirements as-is
    return requires;
  };

  // Check if user can purchase before showing modal
  const checkPurchaseEligibility = async (deal, quantity = 1) => {
    try {
      // First check if deal is available (expiration, supply pool, etc.)
      const { data: availabilityCheck, error: availError } = await supabase
        .rpc('is_deal_available', { p_deal_id: deal.id });
      
      if (availError) {
        console.error('Error checking deal availability:', availError);
        showUserFriendlyError({
          title: 'Availability Check Failed',
          message: 'Unable to check deal availability. Please try again.',
          type: 'error'
        }, 'error');
        return false;
      }
      
      if (!availabilityCheck || availabilityCheck.length === 0 || !availabilityCheck[0].available) {
        const availability = availabilityCheck?.[0] || {};
        let errorTitle = 'Deal Not Available';
        let errorMessage = availability.reason || 'This deal is currently not available.';
        
        if (availability.is_expired) {
          errorTitle = 'Deal Expired';
          errorMessage = 'This deal has expired and is no longer available.';
        } else if (!availability.item_available) {
          errorTitle = 'Sold Out';
          errorMessage = `${deal.deal_name || 'This item'} is currently out of stock.`;
        }
        
        showUserFriendlyError({
          title: errorTitle,
          message: errorMessage,
          type: 'warning'
        }, 'warning');
        return false;
      }
      
      // Then check eligibility (token gates, wallet limits, etc.)
      const eligibilityResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=check-purchase-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId: deal.id,
          walletAddress: wallet,
          quantity: quantity,
          ownedNFTs: nfts // Include owned NFTs for token gating
        })
      });
      
      const eligibilityData = await eligibilityResponse.json();
      
      if (!eligibilityResponse.ok || !eligibilityData.canPurchase) {
        // Show error and stop purchase
        showUserFriendlyError({
          title: 'Purchase Not Allowed',
          message: eligibilityData.error || 'You cannot purchase this item at this time.',
          type: 'error'
        }, 'error');
        return false;
      }
      
      return true;
    } catch (eligibilityError) {
      console.error('Error checking purchase eligibility:', eligibilityError);
      showUserFriendlyError({
        title: 'Eligibility Check Failed',
        message: 'Unable to verify purchase eligibility. Please try again.',
        type: 'error'
      }, 'error');
      return false;
    }
  };

  // Handle DCL purchase from MINT button
  const handleDCLPurchaseFromMintButton = async (deal) => {
    const canPurchase = await checkPurchaseEligibility(deal, 1);
    if (canPurchase) {
      setSelectedDCLDeal(deal);
      setSelectedDCLQuantity(1);
      setShowDCLModal(true);
    }
  };


  // Log DCL purchase attempt
  const logDCLPurchaseAttempt = async (attemptData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=log-purchase-attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attemptData)
      });
    } catch (error) {
      console.error('Failed to log DCL purchase attempt:', error);
    }
  };

  // Handle DCL purchase (actual purchase logic)
  const handleDCLPurchase = async (deal, quantity = 1) => {
    let attemptId = null;
    let userAddress = null;
    
    // Calculate bundle price from deal_items or fallback to single item
    const dealItems = deal.deal_items && deal.deal_items.length > 0 ? deal.deal_items : 
      [{ dcl_price_mana: deal.dcl_price_mana }];
    
    // If RNG is enabled, only charge for one item
    const bundlePrice = deal.rng_enabled ? 
      parseFloat(dealItems[0].dcl_price_mana) : 
      dealItems.reduce((sum, item) => sum + (parseFloat(item.dcl_price_mana) || 0), 0);
    
    const totalPrice = bundlePrice * quantity;
    
    try {
      // Use the wallet prop directly (same as DealsSection)
      userAddress = wallet;
      
      // Log the initial attempt
      const initialAttempt = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=log-purchase-attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: userAddress,
          deal_id: deal.id,
          deal_name: deal.deal_name,
          dcl_collection_address: deal.dcl_collection_address,
          dcl_item_id: deal.dcl_item_id,
          quantity: quantity,
          price_mana: bundlePrice,
          status: 'initiated',
          user_agent: navigator.userAgent
        })
      });
      
      if (initialAttempt.ok) {
        const { attempt } = await initialAttempt.json();
        attemptId = attempt?.id;
      }
      
      // Get the correct provider based on wallet type
      const ethereumProvider = getEthereumProvider();
      
      // Switch to Polygon network
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon mainnet
      });

      // Get provider and signer
      let provider, signer;
      if (selectedWalletType === 'magiceden') {
        const multiChain = window.magicEden?.multiChain;
        if (multiChain && multiChain.isConnected) {
          const multichainResponse = multiChain.currentMultiChainResponse;
          if (multichainResponse?.addresses?.ethereum) {
            const ethAddresses = multichainResponse.addresses.ethereum;
            userAddress = Array.isArray(ethAddresses) ? ethAddresses[0] : ethAddresses;
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
          } else {
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
            userAddress = await signer.getAddress();
          }
        } else {
          provider = new BrowserProvider(ethereumProvider);
          signer = await provider.getSigner();
          userAddress = await signer.getAddress();
        }
      } else {
        provider = new BrowserProvider(ethereumProvider, 137); // Polygon chainId
        userAddress = wallet; // Use connected wallet address
        signer = await getSignerForWallet(provider, 137, userAddress);
      }

      // Check if beneficiary address is valid
      if (!deal.dcl_beneficiary_address) {
        showUserFriendlyError({
          title: 'Invalid Deal Configuration',
          message: 'This deal is missing a beneficiary address. Please contact support.',
          type: 'error'
        }, 'error');
        throw new Error('Missing beneficiary address');
      }

      // Check if user has enough MANA
      const manaContract = new Contract(MANA_ADDRESS, MANA_ABI, signer);
      const balance = await manaContract.balanceOf(userAddress);
      const decimals = await manaContract.decimals();
      const requiredAmount = BigInt(Math.round(totalPrice * Math.pow(10, Number(decimals))));

      if (balance < requiredAmount) {
        const bundleDescription = dealItems.length > 1 
          ? `${bundlePrice} bundle × ${quantity}` 
          : `${bundlePrice} × ${quantity}`;
        const errorMessage = `You need ${totalPrice} MANA (${bundleDescription}) but only have ${Number(balance) / Math.pow(10, Number(decimals))} MANA.`;
        
        showUserFriendlyError({
          title: 'Insufficient MANA Balance',
          message: errorMessage,
          type: 'warning'
        }, 'warning');
        
        throw new Error(`Insufficient MANA: ${errorMessage}`);
      }

      // Send MANA payment to beneficiary
      const paymentTx = await manaContract.transfer(
        deal.dcl_beneficiary_address,
        requiredAmount,
        { gasLimit: 300_000, chainId: 137 }
      );
      
      // Update attempt status to payment_sent
      if (attemptId) {
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: 'payment_sent',
          payment_tx_hash: paymentTx.hash,
          payment_sent_at: new Date().toISOString()
        });
      }

      // Wait for payment confirmation
      const receipt = await paymentTx.wait();
      
      // Update attempt status to payment_confirmed
      if (attemptId) {
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: 'payment_confirmed',
          payment_confirmed_at: new Date().toISOString()
        });
      }

      // Call backend to mint the DCL wearable
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/deals?action=purchase-dcl-wearable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealId: deal.id,
          walletAddress: userAddress,
          paymentTxHash: paymentTx.hash,
          quantity: quantity,
          ownedNFTs: nfts // Include owned NFTs for token gating security
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update attempt status to completed
        if (attemptId) {
          await logDCLPurchaseAttempt({
            id: attemptId,
            status: 'completed',
            mint_tx_hash: result.mintTxHash,
            mint_completed_at: new Date().toISOString()
          });
        }
        
        // Return success with transaction hashes
        return {
          success: true,
          paymentTxHash: paymentTx.hash,
          mintTxHash: result.mintTxHash,
          mintTxHashes: result.mintTxHashes || [result.mintTxHash],
          minted_item: result.minted_item,
          random_value: result.random_value,
          rng_pool: result.rng_pool
        };
      } else {
        throw new Error(result.error || 'Purchase failed');
      }

    } catch (error) {
      console.error('DCL purchase failed:', error);
      
      // Log the failure
      if (attemptId) {
        await logDCLPurchaseAttempt({
          id: attemptId,
          status: (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || error.message?.includes('user denied') || error.message?.includes('User denied') || error.message?.includes('User rejected the request')) ? 'cancelled' : 'failed',
          error_message: error.message,
          error_stage: 'client_side'
        });
      } else {
        await logDCLPurchaseAttempt({
          wallet: userAddress || wallet,
          deal_id: deal.id,
          deal_name: deal.deal_name,
          dcl_collection_address: deal.dcl_collection_address,
          dcl_item_id: deal.dcl_item_id,
          quantity: quantity,
          price_mana: bundlePrice,
          status: (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || error.message?.includes('user denied') || error.message?.includes('User denied') || error.message?.includes('User rejected the request')) ? 'cancelled' : 'failed',
          error_message: error.message,
          error_stage: 'client_side',
          user_agent: navigator.userAgent
        });
      }
      
      // Show appropriate error message
      showUserFriendlyError(error, 'purchase');
      
      throw error;
    }
  };

  // PIN verification function
  const verifyBypassPin = async (pin) => {
    try {
      const { data } = await api.post('/api/admin', { pin }, {
        params: { action: 'verify-bypass-pin' }
      });
      
      if (data.valid) {
        setMaintenanceBypassed(true);
        setShowPinModal(false);
        setPinInput('');
        setPinError('');
        return true;
      } else {
        setPinError('Invalid PIN. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setPinError('Error verifying PIN. Please try again.');
      return false;
    }
  };

  // Handle PIN submission
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pinInput.length < 3) {
      setPinError('PIN must be at least 3 characters.');
      return;
    }
    await verifyBypassPin(pinInput);
  };


  const copyToClipboard = () => {
    navigator.clipboard.writeText(wallet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        alert(`Copied to clipboard:\n${wallet}`);
      });
  };

  const sortedLeaderboard = useMemo(() => {
    return Array.isArray(leaderboard)
      ? [...leaderboard].sort((a, b) => {
        const aPoints = sortMode === 'daily'
          ? a.rarity_score || 0
          : a.total_points || 0;
        const bPoints = sortMode === 'daily'
          ? b.rarity_score || 0
          : b.total_points || 0;
        return bPoints - aPoints;
      })
      : [];
  }, [leaderboard, sortMode]);

  useEffect(() => {
    const fetchUpgrades = async () => {
      try {
        const res = await api.get('/api/admin', { params: { action: 'upgrades' } });
        const allUpgrades = res.data.upgrades || [];
        const activeUpgrades = allUpgrades.filter(u => u.active);
        setUpgrades(activeUpgrades);
      } catch (err) {
        console.error('Error fetching upgrades:', err);
      }
    };
    fetchUpgrades();
  }, []);


  const RARITY_SCORES = {
    unique: 500,
    mythic: 100,
    exotic: 50,
    legendary: 20,
    epic: 10,
    rare: 5,
    uncommon: 2,
    common: 1,
  };

  const sortNFTs = (tokens) => {
    return tokens.sort((a, b) => {
      if (a.isMultiUpgradeable && !b.isMultiUpgradeable) return -1;
      if (!a.isMultiUpgradeable && b.isMultiUpgradeable) return 1;
      if (a.upgradeable && !b.upgradeable) return -1;
      if (!a.upgradeable && b.upgradeable) return 1;
      if (a.title !== b.title) return a.title.localeCompare(b.title);
      const aNum = parseFloat(a.tokenId);
      const bNum = parseFloat(b.tokenId);
      const aIsNumeric = !isNaN(aNum);
      const bIsNumeric = !isNaN(bNum);
      if (aIsNumeric && bIsNumeric) return aNum - bNum;
      if (aIsNumeric && !bIsNumeric) return -1;
      if (!aIsNumeric && bIsNumeric) return 1;
      return a.tokenId.localeCompare(b.tokenId);
    });
  };

  // ── Reset state when wallet disconnects ────────────────────────────
  useEffect(() => {
    if (!wallet) {
      // Clear all wallet-dependent state when disconnecting
      setNfts([]);
      setRaritiesMap([]);
      setSelectedNFTs([]);
      setDeals([]);
      setHasFetchedNfts(false);
      setUpgradeSuccess(false);
      setJustMinted(0);
      initialStatsPosted.current = false;
    }
  }, [wallet]);

  // ── effect A: fetch raw NFTs once when wallet connects ────────────────
  useEffect(() => {
    if (!wallet || hasFetchedNfts) return;    // only when first connecting
    setLoading(true);
    fetchOwnedNFTs(wallet)
      .then(tokens => {
        setNfts(tokens);            // raw tokens
        setRaritiesMap(buildRarities(tokens));
        setLoading(false);
        setHasFetchedNfts(true);
      })
      .catch(error => {
        console.error("Error fetching NFTs:", error);
        setLoading(false);
        setHasFetchedNfts(true);
      });
  }, [wallet, hasFetchedNfts]);

  // ── effect: fetch token balances when wallet connects ─────────────────
  useEffect(() => {
    if (wallet) {
      fetchTokenBalances(wallet);
    }
  }, [wallet]);

  // ── effect B: tag & sort once after NFTs + recipes load ──────────
  useEffect(() => {

    // wait until we've fetched NFTs (we'll still run if recipes is empty)
    if (!hasFetchedNfts || !nfts.length) {
      return;
    }

    const multiReqs = recipes
      .flatMap(r => r.requires || [])
      .map(r => ({
        collection: r.collection.toLowerCase(),
        item_id: r.item_id?.toString()
      }));

    const tagged = nfts.map(n => {
      const coll = n.collection.toLowerCase();
      const id = n.tokenId.toString();
      const isMulti = multiReqs.some(r =>
        r.collection === coll && (!r.item_id || r.item_id === id)
      );
      return { ...n, isMultiUpgradeable: isMulti };
    });

    setNfts(sortNFTs(tagged));
  }, [recipes, hasFetchedNfts]);


  // EFFECT A: post stats once, immediately after initial NFT load
  useEffect(() => {
    if (
      wallet &&
      hasFetchedNfts &&            // NFTs have finished loading
      !initialStatsPosted.current  // and we haven't posted yet
    ) {
      const item_count = nfts.length;
      const upgraded_count = initialUpgradedCount;
      const raritiesArray = flattenRarities(nfts);
      const rarity_score = computeRarityScore(raritiesArray);
      api.post('/api/stats?action=update', {
        wallet,
        item_count,
        upgraded_count,
        // send your live map, not a rebuild from nfts
        rarities: raritiesArray,
        rarity_score
      }, {
        headers: {
          'x-api-access': 'leaderboard-access-2025'
        }
      })
        .then(() => {
          fetchLeaderboard();
          initialStatsPosted.current = true;
        })
        .catch(console.error);
    }
  }, [wallet, hasFetchedNfts]);

  useEffect(() => {
    if (!wallet) return;

    setDealsLoading(true);
    api.get('/api/deals', { params: { address: wallet } })
      .then(({ data }) => {
        // console.log('📦 Fetched deals:', data.deals);
        setDeals(data.deals || []);
      })
      .catch(err => {
        console.error("Error fetching deals:", err);
        setDeals([]);
      })
      .finally(() => {
        setDealsLoading(false);
      });
  }, [wallet]);

  useEffect(() => {
    api.get('/api/admin', { params: { action: 'recipes' } })
      .then(({ data }) => setRecipes(data.recipes || []))
      .catch(err => console.error("Error fetching recipes:", err));
  }, [wallet]);

  // Fetch collection items for name resolution
  useEffect(() => {
    const fetchCollectionItems = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=dcl-collections`);
        const data = await response.json();
        setCollectionItems(data.collections || []);
        
      } catch (error) {
        console.error('Failed to fetch collection items:', error);
      }
    };
    fetchCollectionItems();
  }, []);

  // Check token availability for free mint recipes
  useEffect(() => {
    const checkAllTokenAvailability = async () => {
      if (!wallet || !nfts.length || !upgrades.length) {
        return;
      }
      
      const availability = {};
      const statistics = {};
      
      for (const upgrade of upgrades) {
        if (upgrade.recipe_type === 'free_mint') {
          try {
            const isAvailable = await checkTokenAvailability(upgrade, nfts);
            availability[upgrade.id] = isAvailable;
            
            // Also get claim statistics
            const stats = await getClaimStatistics(upgrade, nfts);
            statistics[upgrade.id] = stats;
          } catch (error) {
            console.error(`Error checking availability for recipe ${upgrade.id}:`, error);
            availability[upgrade.id] = false;
            statistics[upgrade.id] = {
              walletUsed: 0,
              walletLimit: upgrade.limit_per_wallet || 0,
              totalTokenUsed: 0,
              totalTokenLimit: 0,
              tokenCount: 0,
              perTokenLimit: upgrade.limit_per_token || 1,
              perTokenEnabled: false
            };
          }
        }
      }
      
      setTokenAvailability(availability);
      setClaimStatistics(statistics);
    };
    
    checkAllTokenAvailability();
  }, [wallet, nfts, upgrades, collectionItems]);

  // ── Post-mint stats & leaderboard update ─────────────────────────────
  useEffect(() => {
    // only run when we've actually minted something
    if (!wallet || justMinted <= 0) return;

    // "current NFTs – burned + freshly minted"
    const item_count = nfts.length + justMinted;
    const upgraded_count = initialUpgradedCount + justMinted;
    const raritiesArray = flattenRarities(nfts);
    const rarity_score = computeRarityScore(flattenRarities(nfts));

    api.post('/api/stats?action=update', {
      wallet,
      item_count,
      upgraded_count,
      // send your live map, not a rebuild from nfts
      rarities: raritiesArray,
      rarity_score
    }, {
      headers: {
        'x-api-access': 'leaderboard-access-2025'
      }
    })
      .then(() => fetchLeaderboard())
      .catch(console.error)
      .finally(() => {
        // clear the flag so we never loop again
        setInitialUpgradedCount(upgraded_count);
        setJustMinted(0);
      });
  }, [wallet, justMinted]);



  const availableDeals = useMemo(() => {
    return deals.filter(deal => {
      // Both DCL wearable deals and traditional deals use the same NFT requirement logic
      const reqs = Array.isArray(deal.required_items) && deal.required_items.length > 0
        ? deal.required_items.map(req => ({
            ...req,
            // Auto-generate URL if empty and we have collection data
            url: req.url || 
                 (req.collection && req.item_id 
                   ? `https://decentraland.org/marketplace/contracts/${req.collection}/items/${req.item_id}`
                   : req.collection
                     ? `https://decentraland.org/marketplace/browse?assetType=item&vendor=decentraland&contracts=${req.collection}`
                     : null)
          }))
        : [{ 
            collection: deal.collection, 
            item_id: deal.required_item_id,
            name: deal.required_item_name,
            url: deal.required_item_url || 
                 (deal.collection && deal.required_item_id 
                   ? `https://decentraland.org/marketplace/contracts/${deal.collection}/items/${deal.required_item_id}`
                   : null)
          }].filter(req => req.collection);
      
      // If no requirements, deal is available to everyone
      if (!reqs[0]?.collection) return true;
      
      // Use the same token gate validation logic as recipes
      return checkTokenGateAccess(reqs, deal.token_gate_urn_requirements, deal.logic || 'AND', nfts);
    }).sort((a, b) => {
      // Sort so that wearable deals appear first, then phygital deals
      if (a.deal_type === 'dcl_wearable' && b.deal_type !== 'dcl_wearable') return -1;
      if (a.deal_type !== 'dcl_wearable' && b.deal_type === 'dcl_wearable') return 1;
      return 0; // Keep original order for deals of the same type
    });
  }, [deals, nfts]);

  const lockedDeals = useMemo(() => {
    return deals.filter(deal => {
      // Both DCL wearable deals and traditional deals use the same NFT requirement logic
      const reqs = Array.isArray(deal.required_items) && deal.required_items.length > 0
        ? deal.required_items.map(req => ({
            ...req,
            // Auto-generate URL if empty and we have collection data
            url: req.url || 
                 (req.collection && req.item_id 
                   ? `https://decentraland.org/marketplace/contracts/${req.collection}/items/${req.item_id}`
                   : req.collection
                     ? `https://decentraland.org/marketplace/browse?assetType=item&vendor=decentraland&contracts=${req.collection}`
                     : null)
          }))
        : [{ 
            collection: deal.collection, 
            item_id: deal.required_item_id,
            name: deal.required_item_name,
            url: deal.required_item_url || 
                 (deal.collection && deal.required_item_id 
                   ? `https://decentraland.org/marketplace/contracts/${deal.collection}/items/${deal.required_item_id}`
                   : null)
          }].filter(req => req.collection);
      
      // If no requirements, deal is never locked
      if (!reqs[0]?.collection) return false;
      
      // Use the same token gate validation logic as recipes (inverted for locked deals)
      return !checkTokenGateAccess(reqs, deal.token_gate_urn_requirements, deal.logic || 'AND', nfts);
    }).sort((a, b) => {
      // Sort so that wearable deals appear first, then phygital deals
      if (a.deal_type === 'dcl_wearable' && b.deal_type !== 'dcl_wearable') return -1;
      if (a.deal_type !== 'dcl_wearable' && b.deal_type === 'dcl_wearable') return 1;
      return 0; // Keep original order for deals of the same type
    });
  }, [deals, nfts]);

  // Particle effect for dashboard background
  useEffect(() => {
    if (wallet) {  // Only run particles when dashboard is showing
      const createDashboardParticle = () => {
        const particle = document.createElement('div');
        particle.className = 'dashboard-particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDuration = (Math.random() * 3 + 5) + 's';
        particle.style.animationDelay = Math.random() * 1.5 + 's';
        
        // Create different flame sizes - small, medium, large
        const sizeVariations = [
          { width: '4px', height: '12px', scale: 0.8 },   // Small flames
          { width: '5px', height: '15px', scale: 1.0 },   // Medium flames  
          { width: '6px', height: '18px', scale: 1.2 },   // Large flames
          { width: '7px', height: '20px', scale: 1.4 }    // Extra large flames
        ];
        
        const randomSize = sizeVariations[Math.floor(Math.random() * sizeVariations.length)];
        particle.style.width = randomSize.width;
        particle.style.height = randomSize.height;
        particle.style.transform = `scale(${randomSize.scale})`;
        
        // Add slight random blur for flickering effect
        particle.style.filter = `blur(${0.4 + Math.random() * 0.8}px)`;

        const particlesContainer = document.getElementById('dashboard-particles');
        if (particlesContainer) {
          particlesContainer.appendChild(particle);

          setTimeout(() => {
            if (particle.parentNode) {
              particle.remove();
            }
          }, 8000);
        }
      };

      // Generate particles for dashboard
      const dashboardInterval = setInterval(createDashboardParticle, 400);

      // Create initial particles
      for (let i = 0; i < 10; i++) {
        setTimeout(createDashboardParticle, i * 120);
      }

      // Cleanup function
      return () => {
        clearInterval(dashboardInterval);
      };
    }
  }, [wallet]);

  // Particle effect for landing page
  useEffect(() => {
    if (!wallet) {  // Only run particles when showing landing page
      const createLandingParticle = () => {
        const particle = document.createElement('div');
        particle.className = 'landing-particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDuration = (Math.random() * 2 + 3) + 's';
        particle.style.animationDelay = Math.random() * 1.5 + 's';
        
        // Create different flame sizes for landing page
        const sizeVariations = [
          { width: '5px', height: '14px', scale: 0.9 },   // Small flames
          { width: '6px', height: '18px', scale: 1.0 },   // Medium flames  
          { width: '7px', height: '22px', scale: 1.1 },   // Large flames
          { width: '8px', height: '25px', scale: 1.3 }    // Extra large flames
        ];
        
        const randomSize = sizeVariations[Math.floor(Math.random() * sizeVariations.length)];
        particle.style.width = randomSize.width;
        particle.style.height = randomSize.height;
        particle.style.transform = `scale(${randomSize.scale})`;
        
        // Add slight random blur for flickering effect
        particle.style.filter = `blur(${0.6 + Math.random() * 0.9}px)`;

        const particlesContainer = document.getElementById('landing-particles');
        if (particlesContainer) {
          particlesContainer.appendChild(particle);

          setTimeout(() => {
            if (particle.parentNode) {
              particle.remove();
            }
          }, 5000);
        }
      };

      // Generate particles
      particleIntervalRef.current = setInterval(createLandingParticle, 250);

      // Create initial particles
      for (let i = 0; i < 12; i++) {
        setTimeout(createLandingParticle, i * 80);
      }

      // Cleanup function
      return () => {
        if (particleIntervalRef.current) {
          clearInterval(particleIntervalRef.current);
        }
      };
    }
  }, [wallet]);

  // Background music initialization and control (desktop only)
  useEffect(() => {
    // Only initialize audio on desktop devices
    if (isMobile) {

      return;
    }
    
    // Initialize audio when component mounts
    if (!audioRef.current) {
      audioRef.current = new Audio('/bg.mp3');
      audioRef.current.loop = true;
      audioRef.current.volume = volume;
      
      // Attempt to play (will fail if user hasn't interacted yet)
      const playAudio = () => {
        if (audioRef.current && !isMuted) {
          audioRef.current.play().catch(err => {
            // Auto-play blocked, will play after user interaction
          });
        }
      };
      
      // Try to play immediately
      playAudio();
      
      // Also try to play on first user interaction
      const enableAudio = () => {
        playAudio();
        document.removeEventListener('click', enableAudio);
        document.removeEventListener('keydown', enableAudio);
      };
      
      document.addEventListener('click', enableAudio);
      document.addEventListener('keydown', enableAudio);
    }

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isMobile]);

  // Handle mute/unmute (desktop only)
  useEffect(() => {
    if (isMobile || !audioRef.current) return;
    
    if (isMuted) {
      audioRef.current.pause();
    } else {
              audioRef.current.play().catch(err => {
          // Audio play failed (likely due to browser autoplay policy)
        });
    }
  }, [isMuted, isMobile]);

  // Update audio volume when volume state changes (desktop only)
  useEffect(() => {
    if (isMobile || !audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume, isMobile]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const increaseVolume = () => {
    setVolume(prev => Math.min(1, prev + 0.1)); // Max 100%
  };

  const decreaseVolume = () => {
    setVolume(prev => Math.max(0, prev - 0.1)); // Min 0%
  };

  if (!wallet) {
    return (
      <>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
            
            .landing-page {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
              font-family: 'Inter', sans-serif;
              background: linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000);
              background-size: 400% 400%;
              animation: gradientShift 15s ease infinite;
              color: white;
              overflow-x: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
            }
            
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            .landing-container {
              text-align: center;
              max-width: 800px;
              padding: 2rem;
              position: relative;
              margin: 0;
            }
            
            .landing-logo {
              font-size: 4rem;
              font-weight: 800;
              margin-bottom: 1rem;
              background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
              background-size: 400% 400%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: rainbowFlow 3s ease-in-out infinite;
              text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
            }
            
            @keyframes rainbowFlow {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            .landing-badge {
              display: inline-block;
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              border: 1px solid rgba(255, 255, 255, 0.2);
              border-radius: 50px;
              padding: 0.5rem 1.5rem;
              margin-bottom: 2rem;
              font-size: 0.9rem;
              font-weight: 600;
              letter-spacing: 2px;
              text-transform: uppercase;
              animation: pulse 2s ease-in-out infinite;
            }
            
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.8; }
              50% { transform: scale(1.05); opacity: 1; }
            }
            
            .landing-title {
              font-size: 2.5rem;
              font-weight: 700;
              margin-bottom: 1rem;
              opacity: 0;
              animation: slideUp 1s ease 0.5s forwards;
            }
            
            .landing-subtitle {
              font-size: 1.2rem;
              margin-bottom: 3rem;
              opacity: 0;
              animation: slideUp 1s ease 0.7s forwards;
            }
            
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .landing-cta-button {
              display: inline-block;
              padding: 1.2rem 3rem;
              margin-bottom: 4rem;
              background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
              background-size: 400% 400%;
              border: none;
              border-radius: 50px;
              color: white;
              font-size: 1.1rem;
              font-weight: 600;
              text-decoration: none;
              text-transform: uppercase;
              letter-spacing: 1px;
              transition: all 0.3s ease;
              animation: slideUp 1s ease 0.9s forwards, rainbowFlow 3s ease-in-out infinite;
              box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
              opacity: 0;
              cursor: pointer;
            }
            
            .landing-cta-button:hover {
              transform: translateY(-5px);
              box-shadow: 0 20px 40px rgba(255, 255, 255, 0.3);
            }
            
            .landing-cta-button:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              transform: none;
            }
            
            .landing-particles {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
              overflow: hidden;
            }
            
            .landing-particle {
              position: absolute;
              background: linear-gradient(180deg, 
                #8b0000 0%,    /* Dark red base */
                #dc143c 15%,   /* Crimson */
                #ff4500 35%,   /* Orange-red */
                #ff6347 55%,   /* Tomato */
                #ff8c00 75%,   /* Dark orange */
                #ffa500 90%,   /* Orange */
                transparent 100%
              );
              border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
              animation: fireFloat 5s linear infinite;
              filter: blur(0.8px);
              box-shadow: 0 0 8px rgba(255, 69, 0, 0.5);
              opacity: 0;
              visibility: hidden;
            }
            
            @keyframes fireFloat {
              0% {
                transform: translateY(105vh) translateX(0px) scale(0.6) rotate(0deg);
                opacity: 0;
                visibility: hidden;
              }
              5% {
                opacity: 0.8;
                visibility: visible;
                transform: translateY(95vh) translateX(1px) scale(0.7) rotate(1deg);
              }
              15% {
                transform: translateY(85vh) translateX(2px) scale(0.8) rotate(2deg);
              }
              30% {
                transform: translateY(70vh) translateX(-1px) scale(1.0) rotate(-1deg);
              }
              50% {
                transform: translateY(50vh) translateX(3px) scale(1.1) rotate(3deg);
              }
              70% {
                transform: translateY(30vh) translateX(-2px) scale(0.9) rotate(-2deg);
              }
              90% {
                opacity: 0.6;
                transform: translateY(10vh) translateX(1px) scale(0.7) rotate(1deg);
              }
              100% {
                transform: translateY(-10vh) translateX(0px) scale(0.4) rotate(0deg);
                opacity: 0;
                visibility: hidden;
              }
            }
            
            .landing-footer {
              margin-top: 2rem;
              font-size: 0.9rem;
              opacity: 0;
              animation: slideUp 1s ease 1.1s forwards;
            }
            
            .music-controls {
              position: fixed;
              bottom: 2rem;
              left: 2rem;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              z-index: 1001;
            }
            
            .music-toggle {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.15);
              backdrop-filter: blur(15px);
              border: 3px solid rgba(255, 255, 255, 0.3);
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.5rem;
              cursor: pointer;
              transition: all 0.3s ease;
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
              animation: musicPulse 3s ease-in-out infinite;
            }
            
            .volume-controls {
              display: flex;
              gap: 0.25rem;
              justify-content: center;
            }
            
            .volume-btn {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              border: 2px solid rgba(255, 255, 255, 0.2);
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1rem;
              cursor: pointer;
              transition: all 0.3s ease;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
              font-weight: bold;
            }
            
            .volume-btn:hover {
              background: rgba(255, 255, 255, 0.2);
              border-color: rgba(255, 255, 255, 0.4);
              transform: scale(1.1);
            }
            
            .volume-btn:disabled {
              opacity: 0.3;
              cursor: not-allowed;
              transform: none;
            }
            
            .volume-display {
              text-align: center;
              font-size: 0.7rem;
              color: white;
              opacity: 0.8;
              font-weight: 600;
              margin-top: 0.25rem;
              text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
            }
            
            @keyframes musicPulse {
              0%, 100% { 
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(138, 43, 226, 0.7);
              }
              50% { 
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(138, 43, 226, 0);
              }
            }
            
            .music-toggle::before {
              content: '';
              position: absolute;
              top: -3px;
              left: -3px;
              right: -3px;
              bottom: -3px;
              background: linear-gradient(45deg, #8a2be2, #ff1493, #00bfff, #32cd32);
              background-size: 400% 400%;
              animation: rainbowFlow 2s ease-in-out infinite;
              opacity: 0.8;
              border-radius: 50%;
              z-index: -1;
            }
            
            .music-toggle:hover::before {
              opacity: 1;
              animation: rainbowFlow 1s ease-in-out infinite;
            }
            
            .music-toggle:hover {
              transform: scale(1.1);
              box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
              border-color: rgba(255, 255, 255, 0.6);
            }
            
            .music-toggle.muted {
              opacity: 0.5;
              animation: none;
            }

            @media (max-width: 768px) {
              .landing-logo {
                font-size: 2.5rem;
              }
              .landing-title {
                font-size: 1.8rem;
              }
              .landing-subtitle {
                font-size: 1rem;
              }
              .landing-container {
                padding: 1rem;
              }
              .music-controls {
                bottom: 1rem;
                left: 1rem;
              }
              .music-toggle {
                width: 50px;
                height: 50px;
              }
              .music-toggle svg {
                width: 20px;
                height: 20px;
              }
              .volume-btn {
                width: 24px;
                height: 24px;
              }
              .volume-btn svg {
                width: 12px;
                height: 12px;
              }
              .volume-display {
                font-size: 0.6rem;
              }
            }
          `}
          {showSecurityAlert && `
            @keyframes securityAlert {
              0% { 
                opacity: 0; 
                transform: translate(-50%, -50%) scale(0.3) rotateX(90deg);
              }
              50% {
                transform: translate(-50%, -50%) scale(1.1) rotateX(0deg);
              }
              100% { 
                opacity: 1; 
                transform: translate(-50%, -50%) scale(1) rotateX(0deg);
              }
            }
            
            @keyframes alertPulse {
              0%, 100% { 
                box-shadow: 0 20px 60px rgba(255, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1);
                transform: translate(-50%, -50%) scale(1);
              }
              50% { 
                box-shadow: 0 25px 80px rgba(255, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.2), 0 0 30px rgba(255, 0, 0, 0.6);
                transform: translate(-50%, -50%) scale(1.02);
              }
            }
          `}
        </style>
        <div className="landing-page">
          <div className="landing-particles" id="landing-particles"></div>

          <div className="landing-container">
            <div className="landing-badge">NFT Dashboard</div>
            <h1 className="landing-logo">DOCTORdripp</h1>
            <h2 className="landing-title">Digital Collectibles</h2>
            
            {maintenanceMode && !maintenanceBypassed && !(isAdmin && wallet) ? (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000)',
                backgroundSize: '400% 400%',
                animation: 'gradientShift 15s ease infinite',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(10px)'
              }}>
                {/* Crown Icon for Admin Bypass */}
                <div
                  onClick={() => setShowPinModal(true)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    fontSize: '0.8rem',
                    opacity: 0.03,
                    cursor: 'pointer',
                    transition: 'opacity 0.3s ease',
                    zIndex: 1001
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.2'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.03'}
                  title="Admin Access"
                >
                  👑
                </div>
                <div style={{
                  textAlign: 'center',
                  maxWidth: '600px',
                  padding: '3rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: '800',
                    marginBottom: '1rem',
                    background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                    backgroundSize: '400% 400%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'rainbowFlow 3s ease-in-out infinite'
                  }}>
                    🛠️ Maintenance in Progress
                  </h1>
                  <p style={{
                    fontSize: '1.2rem',
                    marginBottom: '2rem',
                    opacity: 0.9,
                    lineHeight: 1.6
                  }}>
                    {maintenanceMessage}
                  </p>
                  <div style={{
                    fontSize: '0.9rem',
                    opacity: 0.6
                  }}>
                    Please check back soon for an enhanced experience!
                  </div>
                </div>
              </div>
            ) : mintLaunchingMode === null ? (
              // Loading state - checking if mint launching mode is active
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000)',
                backgroundSize: '400% 400%',
                animation: 'gradientShift 15s ease infinite',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                color: 'white',
                fontSize: '1.2rem'
              }}>
                Loading...
              </div>
            ) : mintLaunchingMode && mintLaunchingConfig && collectionItems.length > 0 ? (
              // Second-tier: Mint Launching Mode (only shows when regular maintenance is off)
              <MintLaunchingPage 
                config={mintLaunchingConfig} 
                collectionItems={collectionItems} 
                onLaunchComplete={handleLaunchComplete}
              />
            ) : mintLaunchingMode && mintLaunchingConfig ? (
              // Loading state for mint launching mode
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000)',
                backgroundSize: '400% 400%',
                animation: 'gradientShift 15s ease infinite',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                color: 'white',
                fontSize: '1.2rem'
              }}>
                Loading mint launch data...
              </div>
            ) : (
              <>
                <p className="landing-subtitle">Connect an Ethereum Wallet to Browse DOCTORdripp NFTs</p>
            <button
              className="landing-cta-button"
              onClick={onConnectWallet}
              disabled={isAuthenticating}
            >
              {isAuthenticating ? 'Authenticating...' : 'Connect Wallet'}
            </button>
              </>
            )}

            <div className="landing-footer">
              Built with ♥ for the DOCTORdripp community
            </div>
          </div>

          {/* Background Music Controls - Landing Page (Desktop Only) */}
          {!isMobile && (
          <div className="music-controls">
            <div 
              className={`music-toggle ${isMuted ? 'muted' : ''}`}
              onClick={toggleMute}
              title={isMuted ? 'Unmute Background Music' : 'Mute Background Music'}
            >
              {isMuted ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 12C16.5 10.23 15.5 8.71 14 7.97V9.18L16.45 11.63C16.48 11.86 16.5 12.08 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" fill="white"/>
                </svg>
                             ) : (
                 <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px' }}>
                   <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                 </svg>
               )}
            </div>
            
            <div className="volume-controls">
              <button 
                className="volume-btn"
                onClick={decreaseVolume}
                disabled={volume <= 0}
                title="Decrease Volume"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13H5V11H19V13Z" fill="white"/>
                </svg>
              </button>
              <button 
                className="volume-btn"
                onClick={increaseVolume}
                disabled={volume >= 1}
                title="Increase Volume"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white"/>
                </svg>
              </button>
            </div>
            
            <div className="volume-display">
              {Math.round(volume * 100)}%
            </div>
          </div>
          )}

          {showSecurityAlert && (
            <div
              onClick={() => {
                // Allow dismissing by clicking anywhere on the alert
                if (onDismissSecurityAlert) {
                  onDismissSecurityAlert();
                }
              }}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'linear-gradient(135deg, rgba(139, 0, 0, 0.95), rgba(220, 20, 60, 0.95), rgba(255, 69, 0, 0.95))',
                backdropFilter: 'blur(15px)',
                border: '3px solid #FF0000',
                borderRadius: '20px',
                padding: '2rem 3rem',
                zIndex: 10000,
                textAlign: 'center',
                maxWidth: '600px',
                boxShadow: '0 20px 60px rgba(255, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                animation: 'securityAlert 0.6s ease-out, alertPulse 2s ease-in-out infinite 0.6s',
                cursor: 'pointer'
              }}
            >
              <div
                style={{
                  fontSize: '2.2rem',
                  fontWeight: '800',
                  color: 'white',
                  marginBottom: '1rem',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.8), 0 2px 4px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))'
                }}
              >
                ⚠️ SECURITY NOTICE ⚠️
              </div>
              <div
                style={{
                  fontSize: '1.1rem',
                  color: 'white',
                  lineHeight: '1.6',
                  fontWeight: '600',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
                }}
              >
                {disconnectInstructions?.instructions || 
                 'For complete wallet security, click the DOCTORdripp logo in the top-right corner of your MetaMask extension and select "Disconnect" to fully remove this site\'s access permissions.'}
              </div>
              <div
                style={{
                  fontSize: '0.9rem',
                  color: '#FFD700',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
                }}
              >
                Click anywhere to dismiss
              </div>
            </div>
          )}

          {/* PIN Modal for Admin Bypass */}
          {showPinModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              backdropFilter: 'blur(5px)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                padding: '2rem',
                textAlign: 'center',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  marginBottom: '1rem',
                  color: 'white'
                }}>
                  👑 Admin Access
                </h2>
                <p style={{
                  fontSize: '1rem',
                  marginBottom: '1.5rem',
                  opacity: 0.8,
                  color: 'white'
                }}>
                  Enter the bypass PIN to access the dashboard during maintenance.
                </p>
                <form onSubmit={handlePinSubmit}>
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => {
                      setPinInput(e.target.value);
                      setPinError('');
                    }}
                    placeholder="Enter PIN"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1.2rem',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      marginBottom: '1rem',
                      outline: 'none'
                    }}
                    maxLength="20"
                    autoFocus
                  />
                  {pinError && (
                    <div style={{
                      color: '#ff6b6b',
                      fontSize: '0.9rem',
                      marginBottom: '1rem'
                    }}>
                      {pinError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPinModal(false);
                        setPinInput('');
                        setPinError('');
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '10px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '10px',
                        background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Access
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }


  const toggleNFTSelection = (nft) => {
    if (!nft.upgradeable && !nft.isMultiUpgradeable) return;
    const exists = selectedNFTs.find(
      (item) =>
        item.tokenId.toString() === nft.tokenId.toString() &&
        item.collection.toLowerCase() === nft.collection.toLowerCase()
    );
    if (exists) {
      setSelectedNFTs(selectedNFTs.filter(
        (item) =>
          !(item.tokenId.toString() === nft.tokenId.toString() &&
            item.collection.toLowerCase() === nft.collection.toLowerCase())
      ));
    } else {
      setSelectedNFTs([...selectedNFTs, nft]);
    }
  };

  const openUpgradeModal = () => {
    if (selectedNFTs.length === 0) {
      showUserFriendlyError(
        { message: "Please select at least one NFT to upgrade." },
        'upgrade'
      );
      return;
    }
    setShowUpgradeModal(true);
  };

  const closeModal = () => {
    setShowUpgradeModal(false);
  };

  async function handleShopNow(deal) {
    try {
      // First, get the deal access details
      const { data: accessData } = await api.get('/api/deals', {
        params: { 
          action: 'verify-access', 
          dealId: deal.id, 
          walletAddress: wallet 
        }
      });

      // Determine the target URL - prioritize exclusive product URL, fallback to discount link
      let targetUrl = accessData.exclusiveProductUrl || accessData.discountLink;
      
      if (!targetUrl) {
        throw new Error('No product URL available for this deal');
      }

      // If we have an exclusive product URL, get a JWT token for it
      if (accessData.exclusiveProductUrl) {
        const { data: tokenData } = await api.get('/api/deals', {
          params: { action: 'token', dealName: deal.deal_name, wallet }
        });

        // build the redirect URL, preserving any existing ?variant=… param
        try {
          const url = new URL(targetUrl);
          url.searchParams.set('access_token', tokenData.accessToken);
          const currentVariant = new URLSearchParams(window.location.search).get('variant');
          if (currentVariant) url.searchParams.set('variant', currentVariant);
          window.location.href = url.toString();
        } catch {
          // fallback if targetUrl wasn't an absolute URL
          const sep = targetUrl.includes('?') ? '&' : '?';
          window.location.href =
            `${targetUrl}${sep}access_token=${encodeURIComponent(tokenData.accessToken)}`;
        }
      } else {
        // For discount links, just redirect directly
        window.location.href = targetUrl;
      }
      
    } catch (err) {
      console.error('Error accessing deal:', err);
      showUserFriendlyError(
        { message: 'Unable to access this deal right now. Please try again later.' },
        'deal'
      );
    }
  }


  const confirmUpgrade = async () => {
    let success, minted;
    let userAddress = null; // Declare outside try block so it's available in catch
    
    if (selectedNFTs.length === 0) {
      showUserFriendlyError(
        { message: 'Please select an NFT to upgrade.' },
        'upgrade'
      );
      return;
    }

    try {
      // Get the correct provider based on wallet type
      const ethereumProvider = getEthereumProvider();
      
      // For all wallets including Magic Eden, switch to Polygon network
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }],
      });

      // For Magic Eden, get address from multichain but use ethereum provider for transactions
      let provider, signer;
      if (selectedWalletType === 'magiceden') {
        const multiChain = window.magicEden?.multiChain;
        if (multiChain && multiChain.isConnected) {
          // Magic Eden multiChain detected - getting EVM address
          const multichainResponse = multiChain.currentMultiChainResponse;
          if (multichainResponse?.addresses?.ethereum) {
            // Magic Eden returns addresses as an array, take the first one
            const ethAddresses = multichainResponse.addresses.ethereum;
            userAddress = Array.isArray(ethAddresses) ? ethAddresses[0] : ethAddresses;
            // Use ethereum provider for actual transactions (Magic Eden pattern)
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
          } else {
            // Magic Eden multiChain missing EVM address, falling back to ethereum provider
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
            userAddress = await signer.getAddress();
          }
        } else {
          provider = new BrowserProvider(ethereumProvider, 137);
          userAddress = wallet;
          signer = await getSignerForWallet(provider, 137, userAddress);
        }
      } else {
        // For MetaMask, Coinbase, WalletConnect, etc.
        provider = new BrowserProvider(ethereumProvider, 137); // Polygon chainId
        userAddress = wallet; // Use connected wallet address
        signer = await getSignerForWallet(provider, 137, userAddress);
      }

      let reservedLocks = [];

      // STEP 1: Reserve locks BEFORE burning to prevent race conditions
      try {
        const lockResponse = await api.post(
          '/api/nft',
          { 
            selectedNFTs: selectedNFTs.map(nft => ({
              collection: nft.collection,
              tokenId: nft.tokenId
            })),
            walletAddress: userAddress
          },
          { params: { action: 'reserve-single-locks' } }
        );
        
        reservedLocks = lockResponse.data.reservedLocks;
      } catch (lockError) {
        if (lockError.response?.status === 409) {
          throw new Error(lockError.response.data.error || 'One of your NFTs is already being processed. Please wait and try again.');
        }
        throw new Error(`Failed to reserve locks: ${lockError.response?.data?.error || lockError.message}`);
      }

      // STEP 2: Now burn the NFTs (we already have locks, so WebSocket can't interfere)

      // 2) Group selected NFTs by collection
      const grouped = selectedNFTs.reduce((acc, nft) => {
        acc[nft.collection] = acc[nft.collection] || [];
        acc[nft.collection].push(nft);
        return acc;
      }, {});

      // 3) For each collection, fetch its burn address & queue up all burns
      const burnTxs = [];
      const burnNfts = [];

      for (const coll in grouped) {

        // 3.1 fetch your burn-destination
        const destRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/nft?` +
          new URLSearchParams({ action: 'upgrade-destination', collection: coll })
        );
        const { upgrade_destination: rawDest } = await destRes.json();
        if (!rawDest) throw new Error(`No upgrades available for ${coll} - if you are trying to craft a recipe, be sure to select all required items before clicking CRAFT`);

        // 3.2 normalize it
        const burnDestination =
          rawDest.trim().toUpperCase() === "BURN"
            ? "0x000000000000000000000000000000000000dEaD"
            : rawDest.trim();
        if (!isAddress(burnDestination)) {
          console.error('[confirmUpgrade] ✖ Invalid burnDestination:', burnDestination);
          throw new Error(`Invalid burn address: ${burnDestination}`);
        }

        // 3.3 prepare contract
        const nftContract = new Contract(
          coll,
          ["function safeTransferFrom(address from, address to, uint256 tokenId)"],
          signer
        );

        // 3.4 queue the real transfer calls (no more callStatic)
        for (const nft of grouped[coll]) {
          burnNfts.push(nft);
          const txOptions = { gasLimit: 300_000, chainId: 137 }; // Polygon mainnet
          
          // Use standard ethereum provider for all wallets (Magic Eden EVM pattern)
          burnTxs.push(
            nftContract.safeTransferFrom(
              userAddress,
              burnDestination,
              BigInt(nft.tokenId),
              txOptions
            )
          );
        }

      }

      // 4) Execute burns one by one so you can capture each txHash
      const burnLogs = [];
      for (let i = 0; i < burnTxs.length; i++) {
        const nft = burnNfts[i];
        try {
          const txResponse = await burnTxs[i];
          const receipt = await txResponse.wait();
          
          // Find the reserved lock key for this NFT
          const reservedLock = reservedLocks.find(lock => 
            lock.collection.toLowerCase() === nft.collection.toLowerCase() && 
            lock.tokenId.toString() === nft.tokenId.toString()
          );
          
          burnLogs.push({
            collection: nft.collection.toLowerCase(),
            tokenId: nft.tokenId.toString(),
            txHash: txResponse.hash,
            reservedLockKey: reservedLock?.lockKey
          });
        } catch (err) {
          console.error(
            `[confirmUpgrade] ❌ burn FAILED for ${nft.tokenId}:`,
            err
          );
          throw err; // bubble it up or handle as you see fit
        }
      }

      // 5) Now for each collection, fetch upgrade options and mint _one for each burned token_
      const upgradesPayload = [];
      for (const coll in grouped) {
        const nftGroup = grouped[coll]; // e.g. you burned 3 from this collection

        // fetch same upgrade-options endpoint
        const optRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/nft?` +
          new URLSearchParams({ action: 'upgrade-destination', collection: coll })
        );
        let { upgrade_options: options } = await optRes.json();
        if (typeof options === 'string') options = JSON.parse(options);
        if (!Array.isArray(options) || options.length === 0) {
          throw new Error(`No upgrade options for ${coll}`);
        }

        // flatten into exactly `quantity * nftGroup.length` entries
        options.forEach(opt => {
          const qty = Number(opt.quantity) || 1;
          const totalQty = qty * nftGroup.length;
          for (let i = 0; i < totalQty; i++) {
            upgradesPayload.push({
              targetCollection: opt.targetCollection.trim(),
              upgradedItemId: opt.upgradedItemId
            });
          }
        });
      }

      // ─── call your mint-upgraded endpoint ───────────────────────────
      const mintRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/nft?` +
        new URLSearchParams({ action: 'mint-upgraded' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet: userAddress,
            upgrades: upgradesPayload,
            burns: burnLogs      // ← include the burn receipts with reserved lock keys
          })
        }
      );

      ({ success, minted } = await mintRes.json());
      if (!success) throw new Error(`Minting failed`);

      // 6) Tally & fire your stats / success flow
      const mintedCount = upgradesPayload.length;
      setJustMinted(mintedCount);
      setUpgradeSuccess(true);

      // snapshot which NFTs to remove
      const toRemove = [...selectedNFTs];

      setNfts(prev => {
        // 1) Build the new grid minus the burned items
        const updatedGrid = prev.filter(n =>
          !toRemove.some(sel =>
            sel.tokenId.toString() === n.tokenId.toString() &&
            sel.collection.toLowerCase() === n.collection.toLowerCase()
          )
        );

        // 2) Recompute a fresh rarity-map from the remaining NFTs
        const baseMap = buildRarities(updatedGrid);

        // 3) Merge in the freshly minted traits
        minted.forEach(({ rarities = [] }) => {
          rarities.forEach(({ trait_type, value }) => {
            baseMap[trait_type] = baseMap[trait_type] || {};
            baseMap[trait_type][value] = (baseMap[trait_type][value] || 0) + 1;
          });
        });

        // 4) Commit the live map
        setRaritiesMap(baseMap);

        // 5) Return the updated grid for state
        return updatedGrid;
      });

      // Format the minted item for the modal with available upgrade information
      const mintedItem = minted?.[0] ? {
        name: minted[0].name || minted[0].collectionTitle || upgradesPayload[0]?.targetCollection || minted[0].collection,
        collection: minted[0].collection,
        token_id: minted[0].tokenId,
        image: minted[0].image, // NFT image from backend metadata fetch
        tx_hash: minted[0].txHash, // Mint transaction hash from backend response
        burn_tx_hashes: minted[0].burnTxHashes || [], // Burn transaction hashes
        burn_token_ids: minted[0].burnTokenIds || [], // Burned token IDs
        rarity: minted[0].rarities ? minted[0].rarities.find(r => r.trait_type === 'rarity')?.value : null,
        all_minted_items: minted.map(item => ({
          name: item.name || item.collectionTitle || item.collection,
          collection: item.collection,
          token_id: item.tokenId,
          tx_hash: item.txHash,
          image: item.image, // NFT image from backend metadata fetch
          rarity: item.rarities ? item.rarities.find(r => r.trait_type === 'rarity')?.value : null
        }))
      } : null;

      // ← Add this return so UpgradeModal knows it's done:
      return { success: true, minted: mintedCount, minted_item: mintedItem };

      // NOTE: don't clear selectedNFTs here—leave that to your modal's onDone

    } catch (error) {
      console.error("Error upgrading NFT(s):", error);
      
      // Log the client-side failure
      try {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/nft?` +
          new URLSearchParams({ action: 'log-upgrade-failure' }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet: userAddress || wallet,
              upgrade_type: 'single_upgrade',
              original_nfts: selectedNFTs.map(nft => ({
                collection: nft.collection,
                tokenId: nft.tokenId.toString()
              })),
              error_message: error.message,
              error_stage: 'client_side',
              user_agent: navigator.userAgent
            })
          }
        );
      } catch (logError) {
        console.error('Failed to log upgrade failure:', logError);
      }
      
      showUserFriendlyError(error, 'upgrade');
      return { success: false, minted: 0 };
    }
  };



  // NEW: composite recipes → issue-tokens
  const handleRecipeMint = async (recipe) => {
    let success, minted, random_value, rng_pool;
    let userAddress = null; // Declare outside try block so it's available in catch
    let burns = []; // Declare outside try block so it's available in formatting section
    
    // For free mint recipes, no NFTs need to be selected
    if (recipe.recipe_type !== 'free_mint' && selectedNFTs.length === 0) {
      showUserFriendlyError(
        { message: 'Please select the required NFTs for this recipe before crafting.' },
        'craft'
      );
      return;
    }
    
    // Check if recipe is available (expiration, supply pool, strict mode, etc.)
    try {
      const { data: availabilityCheck, error: availError } = await supabase
        .rpc('is_recipe_available', { p_recipe_id: recipe.id });
      
      if (availError) {
        console.error('Error checking recipe availability:', availError);
        showUserFriendlyError({
          title: 'Availability Check Failed',
          message: 'Unable to check recipe availability. Please try again.',
          type: 'error'
        }, 'error');
        return { success: false, minted: 0 };
      }
      
      if (!availabilityCheck || availabilityCheck.length === 0 || !availabilityCheck[0].available) {
        const availability = availabilityCheck?.[0] || {};
        let errorTitle = 'Recipe Not Available';
        let errorMessage = availability.reason || 'This recipe is currently not available.';
        
        if (availability.is_expired) {
          errorTitle = 'Recipe Expired';
          errorMessage = 'This recipe has expired and is no longer available.';
        } else if (availability.strict_mode && availability.available_rewards_count < availability.total_rewards_count) {
          errorTitle = 'Items Out of Stock';
          errorMessage = `Some items are out of stock (${availability.available_rewards_count} of ${availability.total_rewards_count} available). This recipe uses strict mode.`;
        } else if (availability.available_rewards_count === 0) {
          errorTitle = 'Sold Out';
          errorMessage = 'All items for this recipe have been claimed.';
        }
        
        showUserFriendlyError({
          title: errorTitle,
          message: errorMessage,
          type: 'warning'
        }, 'warning');
        return { success: false, minted: 0 };
      }
      
      console.log(`✅ Recipe ${recipe.id} is available:`, availabilityCheck[0]);
    } catch (availCheckError) {
      console.error('Error checking recipe availability:', availCheckError);
      showUserFriendlyError({
        title: 'Availability Check Failed',
        message: 'Unable to check recipe availability. Please try again.',
        type: 'error'
      }, 'error');
      return { success: false, minted: 0 };
    }

    try {
      // Get the correct provider based on wallet type
      const ethereumProvider = getEthereumProvider();
      
      // For all wallets including Magic Eden, switch to Polygon network
      // (Magic Eden EVM provider supports wallet_switchEthereumChain per their sandbox)
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }],
      });

      // For Magic Eden, get address from multichain but use ethereum provider for transactions
      let provider, signer;
      if (selectedWalletType === 'magiceden') {
        const multiChain = window.magicEden?.multiChain;
        if (multiChain && multiChain.isConnected) {
          // Magic Eden multiChain detected - getting EVM address
          // Get ethereum address from multichain (works for Polygon)
          const multichainResponse = multiChain.currentMultiChainResponse;
          if (multichainResponse?.addresses?.ethereum) {
            // Magic Eden returns addresses as an array, take the first one
            const ethAddresses = multichainResponse.addresses.ethereum;
            userAddress = Array.isArray(ethAddresses) ? ethAddresses[0] : ethAddresses;
            // Use ethereum provider for actual transactions (Magic Eden pattern)
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
          } else {
            // Magic Eden multiChain missing EVM address, falling back to ethereum provider
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
            userAddress = await signer.getAddress();
          }
        } else {
          console.log('⚠️ Magic Eden multiChain not available, using ethereum provider');
          provider = new BrowserProvider(ethereumProvider, 137);
          userAddress = wallet;
          signer = await getSignerForWallet(provider, 137, userAddress);
        }
      } else {
        // For MetaMask, Coinbase, WalletConnect, etc.
        provider = new BrowserProvider(ethereumProvider, 137); // Polygon chainId
        userAddress = wallet; // Use connected wallet address
        signer = await getSignerForWallet(provider, 137, userAddress);
      }

      // Check recipe limits BEFORE any transactions (for all recipe types)
      if (recipe.id) {
        try {
          await api.get('/api/nft', {
            params: { 
              action: 'check-limits',
              recipeId: recipe.id,
              walletAddress: userAddress
            }
          });
        } catch (limitError) {
          if (limitError.response?.status === 429) {
            throw new Error(limitError.response.data?.reason || 'You have reached the limit for this recipe.');
          }
          throw new Error(`Limit check failed: ${limitError.response?.data?.error || limitError.message}`);
        }
      }

      // For free mints, require signature to prevent spam (after limit check)
      if (recipe.recipe_type === 'free_mint') {
        const message = `Free mint: ${recipe.title}\nWallet: ${userAddress}\nTimestamp: ${Date.now()}`;
        try {
          // Always use the signer since we now have it for all wallet types
          await signer.signMessage(message);
        } catch (signError) {
          if (signError.code === 4001 || signError.code === -32603 || signError.code === 'ACTION_REJECTED' || signError.message?.includes('ethers-user-denied') || signError.message?.includes('User rejected the request')) {
            throw new Error('Signature required to proceed with free mint');
          }
          throw signError;
        }
      }

      let reservedLocks = [];

      // 2) For recipes requiring burns, handle the burning process
      if (recipe.recipe_type !== 'free_mint' && selectedNFTs.length > 0) {
        // STEP 1: Reserve locks BEFORE burning to prevent race conditions
        try {
          const lockResponse = await api.post(
            '/api/nft',
            { 
              selectedNFTs: selectedNFTs.map(nft => ({
                collection: nft.collection,
                tokenId: nft.tokenId
              })),
              walletAddress: userAddress,
              recipeId: recipe.id
            },
            { params: { action: 'reserve-recipe-locks' } }
          );
          
          reservedLocks = lockResponse.data.reservedLocks;
        } catch (lockError) {
          if (lockError.response?.status === 409) {
            throw new Error(lockError.response.data.error || 'One of your NFTs is already being processed. Please wait and try again.');
          }
          throw new Error(`Failed to reserve locks: ${lockError.response?.data?.error || lockError.message}`);
        }

        // STEP 2: Now burn the NFTs (we already have locks, so WebSocket can't interfere)
        
        // Build burn transactions using recipe's single upgrade_destination
        const rawDest = recipe.upgrade_destination;
        if (!rawDest) {
          throw new Error(`Recipe "${recipe.title}" has no burn destination configured`);
        }
        const dest = rawDest.trim().toUpperCase() === 'BURN'
          ? '0x000000000000000000000000000000000000dEaD'
          : rawDest.trim();
        if (!isAddress(dest)) {
          throw new Error(`Invalid burn address for recipe: ${dest}`);
        }

        // prepare each burn call
        const burnTxs = selectedNFTs.map(async (nft) => {
          const txOptions = { gasLimit: 300_000, chainId: 137 }; // Polygon mainnet
          
          // Use standard ethereum provider for all wallets (Magic Eden EVM pattern)
          const nftContract = new Contract(
            nft.collection,
            ['function safeTransferFrom(address from, address to, uint256 tokenId)'],
            signer
          );
          
          return nftContract.safeTransferFrom(
            userAddress,
            dest,
            BigInt(nft.tokenId),
            txOptions
          );
        });

        // execute burns and collect their hashes
        const burnTxHashes = [];
        for (const txPromise of burnTxs) {
          const tx = await txPromise;
          if (!tx?.hash) throw new Error('Burn tx failed to return a hash');
          burnTxHashes.push(tx.hash);
          await tx.wait();
        }

        // Build the "burns" array of objects matching NFT + its txHash + reserved lock key
        burns = selectedNFTs.map((nft, i) => ({
          collection: nft.collection.toLowerCase(),
          tokenId: nft.tokenId.toString(),
          txHash: burnTxHashes[i],
          reservedLockKey: reservedLocks.find(lock => 
            lock.collection.toLowerCase() === nft.collection.toLowerCase() && 
            lock.tokenId.toString() === nft.tokenId.toString()
          )?.lockKey
        }));


      }

      // 4) Build the recipe reward entries
      //    { collection, itemId } per reward
      //    Let the backend handle RNG selection for security and audit purposes
      const entries = recipe.rewards.flatMap(r => {
        // fallback to "0" when item_id is null, undefined, or empty
        const raw = r.item_id?.toString().trim();
        const id = raw && raw.length > 0 ? raw : '0';
        const qty = Number(r.quantity) || 1;
        return Array(qty).fill({
          collection: r.collection,
          itemId: id
        });
      });

      // 5) Extract qualifying token IDs for token-gated and free mint recipes (security feature)
      let qualifyingTokenIds = [];
      if (recipe.recipe_type === 'token_gated' || recipe.recipe_type === 'free_mint') {
        // For token-gated recipes, find NFTs that qualify for the token gates
        if (recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0) {
          for (const [index, gate] of recipe.token_gate_requirements.entries()) {
            const requiredCollection = gate.collection.toLowerCase();
            const requiredItemId = gate.item_id ? gate.item_id.toString() : null;
            
            // Get corresponding URN requirement if it exists
            const urnRequirement = recipe.token_gate_urn_requirements && recipe.token_gate_urn_requirements[index];
            const requiredUrnItemId = urnRequirement?.urn_item_id ? urnRequirement.urn_item_id.toString() : null;
            
            const matchingNFTs = nfts.filter(nft => {
              if (nft.collection.toLowerCase() !== requiredCollection) return false;
              
              // If specific item_id is required (for locking to specific blockchain token ID), check that first
              if (requiredItemId && nft.tokenId.toString() !== requiredItemId) {
                return false;
              }
              
              // If URN item ID is specified, check against URN item ID from collection_items
              if (requiredUrnItemId) {
                // First try to find exact match in collection_items
                let collectionItem = collectionItems.find(
                  item => item.collection_address.toLowerCase() === nft.collection.toLowerCase() && 
                          item.token_id === nft.tokenId.toString()
                );
                
                // If no exact match, try client-side URN detection
                if (!collectionItem) {
                  const detectedUrn = detectUrnItemId(nft, collectionItems);
                  if (detectedUrn) {
                    // Create a virtual collection item for this NFT
                    collectionItem = { urn_item_id: detectedUrn };
                    console.log(`🎯 Recipe qualification: URN detected for NFT ${nft.tokenId} → URN ${detectedUrn}`);
                  }
                }
                
                if (!collectionItem) {
                  console.log(`🚫 Recipe qualification: No collection item found for NFT ${nft.collection}#${nft.tokenId}`);
                  return false;
                }
                
                // Check if the URN item ID matches the required URN item ID
                if (collectionItem.urn_item_id.toString() !== requiredUrnItemId) {
                  console.log(`🚫 Recipe qualification: NFT ${nft.tokenId} has urn_item_id ${collectionItem.urn_item_id}, required URN ${requiredUrnItemId}`);
                  return false;
                }
              }
              
              console.log(`✅ Recipe qualification: NFT ${nft.tokenId} matches requirement (blockchain token: ${requiredItemId || 'any'}, URN: ${requiredUrnItemId || 'any'})`);
              return true;
            });
            
            if (recipe.recipe_type === 'free_mint') {
              // For FREE MINT: Send all qualifying NFTs, backend will pick first unused one
              qualifyingTokenIds.push(...matchingNFTs.map(nft => nft.tokenId.toString()));
            } else {
              // For TOKEN-GATED: Use the selected NFTs that also qualify for token gates
              const selectedQualifyingNFTs = selectedNFTs.filter(selectedNft => {
                return matchingNFTs.some(matchingNft => 
                  matchingNft.tokenId.toString() === selectedNft.tokenId.toString() &&
                  matchingNft.collection.toLowerCase() === selectedNft.collection.toLowerCase()
                );
              });
              
              // Add the token IDs of the selected qualifying NFTs
              qualifyingTokenIds.push(...selectedQualifyingNFTs.map(nft => nft.tokenId.toString()));
            }
          }
        }
        console.log(`🔐 ${recipe.recipe_type} recipe qualifying token IDs:`, qualifyingTokenIds);
      }

      // 6) Call your backend to mint them and grab the `minted` array
      const response = await api.post(
        '/api/nft',
        { 
          beneficiaries: [userAddress], 
          entries, 
          burns, 
          recipeId: recipe.id, 
          recipeType: recipe.recipe_type,
          qualifyingTokenIds: qualifyingTokenIds
        },
        { params: { action: 'issue-tokens' } }
      );
      
      ({ success, minted, random_value, rng_pool } = response.data);
      if (!success) {
        throw new Error('Failed to craft recipe');
      }

      // Debug: Log the minted array to see what we're getting
      // console.log('🔍 DEBUG: Minted array from backend:', minted);

      // 6.5) Tally & fire your stats / success flow, exactly like single-upgrade:
      const craftedCount = recipe.rewards.reduce(
        (sum, r) => sum + (Number(r.quantity) || 1),
        0
      );
      setJustMinted(craftedCount);
      setUpgradeSuccess(true);

      // Remove burned NFTs, rebuild rarity map, then merge in minted traits
      const toRemove = [...selectedNFTs];

      setNfts(prev => {
        // 1) Filter out burns
        const updatedGrid = prev.filter(n =>
          !toRemove.some(sel =>
            sel.tokenId.toString() === n.tokenId.toString() &&
            sel.collection.toLowerCase() === n.collection.toLowerCase()
          )
        );

        // 2) Rebuild base rarity map
        const baseMap = buildRarities(updatedGrid);

        // 3) Merge in the freshly minted traits
        minted.forEach(({ rarities = [] }) => {
          rarities.forEach(({ trait_type, value }) => {
            baseMap[trait_type] = baseMap[trait_type] || {};
            baseMap[trait_type][value] = (baseMap[trait_type][value] || 0) + 1;
          });
        });

        // 4) Commit updated rarity map
        setRaritiesMap(baseMap);

        // 5) Return the new grid
        return updatedGrid;
      });

    } catch (err) {
      console.error('Error crafting recipe:', err);
      
      // Show user-friendly error modal instead of alert
      showUserFriendlyError(err, 'craft');
      
      // Log the client-side failure for recipes
      try {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/nft?` +
          new URLSearchParams({ action: 'log-upgrade-failure' }),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              wallet: userAddress || wallet,
              upgrade_type: recipe.recipe_type === 'free_mint' ? 'free_mint_upgrade' : 
                          recipe.recipe_type === 'token_gated' ? 'token_gated_upgrade' : 'recipe_upgrade',
              original_nfts: selectedNFTs.map(nft => ({
                collection: nft.collection,
                tokenId: nft.tokenId.toString()
              })),
              target_recipe: recipe.title,
              recipe_type: recipe.recipe_type,
              error_message: err.response?.data?.reason || err.message,
              error_stage: 'client_side',
              user_agent: navigator.userAgent
            })
          }
        );
      } catch (logError) {
        console.error('Failed to log recipe failure:', logError);
      }
      
      // re-throw so the promise rejects and the modal never jumps to complete
      throw err;
    }

    // Format the minted item for the modal
    const mintedItem = minted?.[0] ? {
      name: minted[0].selectedItemName || recipe.title,
      collection: minted[0].collection,
      token_id: minted[0].tokenId,
      image: minted[0].selectedItemImage || (() => {
        // For RNG recipes, if selectedItemImage is not available, try to get the correct urn-specific image
        // by looking up the minted token ID in collection items
        if (recipe.rng_enabled && minted[0].tokenId && minted[0].collection) {
          const itemInfo = getCollectionItemInfo(collectionItems, minted[0].collection, minted[0].tokenId);
          return itemInfo.image;
        }
        // Fallback to first reward image only if not RNG
        return recipe.rewards[0]?.image || null;
      })(),
      tx_hash: minted[0].txHash, // Mint transaction hash from backend response
      burn_tx_hashes: burns.map(burn => burn.txHash), // Burn transaction hashes (empty for free mint)
      burn_token_ids: burns.map(burn => burn.tokenId), // Burned token IDs (empty for free mint)
      all_minted_items: minted.map(item => ({
        name: item.selectedItemName || (() => {
          // For RNG recipes, if selectedItemName is not available, try to get the correct urn-specific name
          if (recipe.rng_enabled && item.tokenId && item.collection) {
            const itemInfo = getCollectionItemInfo(collectionItems, item.collection, item.tokenId);
            return itemInfo.name;
          }
          return recipe.title;
        })(),
        collection: item.collection,
        token_id: item.tokenId,
        tx_hash: item.txHash,
        image: item.selectedItemImage || (() => {
          // For RNG recipes, if selectedItemImage is not available, try to get the correct urn-specific image
          if (recipe.rng_enabled && item.tokenId && item.collection) {
            const itemInfo = getCollectionItemInfo(collectionItems, item.collection, item.tokenId);
            return itemInfo.image;
          }
          // Fallback to first reward image only if not RNG
          return recipe.rewards[0]?.image || null;
        })()
      }))
    } : null;

    // Debug: Log the formatted minted item
    // console.log('🔍 DEBUG: Formatted mintedItem:', mintedItem);

    return { success: true, minted, minted_item: mintedItem, random_value, rng_pool };
  };

  // 1) Figure out if there's a *complete* recipe in your current selection:
  const matchedRecipe = upgrades.find(u => {
    if (u.type !== 'recipe') return false;

    // Exclude free_mint recipes from floating button logic
    if (u.recipe_type === 'free_mint') {
      return false;
    }

    // Check token gate access first for token-gated recipes
    if (u.recipe_type === 'token_gated') {
      const hasAccess = checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts);
      if (!hasAccess) return false;
    }

    // For normal recipes and token_gated recipes, check burn requirements
    if (!Array.isArray(u.requires) || u.requires.length === 0) return false;

    const logic = u.logic || 'AND';

    if (logic === 'OR') {
      // OR logic: ANY requirement satisfied
      return u.requires.some(req => {
        const coll = req.collection.toLowerCase();
        const reqId = req.item_id ? req.item_id.toString() : null;
        const neededQty = req.quantity || 1;

        const matchingNFTs = selectedNFTs.filter(n => {
          if (n.collection.toLowerCase() !== coll) return false;
          return reqId ? n.tokenId.toString() === reqId : true;
        });

        return matchingNFTs.length >= neededQty;
      });
    } else {
      // AND logic: ALL requirements satisfied AND exact count match
      const allRequirementsMet = u.requires.every(req => {
        const coll = req.collection.toLowerCase();
        const reqId = req.item_id ? req.item_id.toString() : null;
        const neededQty = req.quantity || 1;

        const matchingNFTs = selectedNFTs.filter(n => {
          if (n.collection.toLowerCase() !== coll) return false;
          return reqId ? n.tokenId.toString() === reqId : true;
        });

        return matchingNFTs.length >= neededQty;
      });

      // For AND logic, also verify the exact count matches
      const totalRequired = u.requires.reduce((sum, req) => sum + (req.quantity || 1), 0);
      return allRequirementsMet && selectedNFTs.length === totalRequired;
    }
  });

  // 2) Now a simple boolean flag
  const isRecipeFlow = Boolean(matchedRecipe);

  return (
    <div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
          
          .nftDashboardContainer {
            min-height: 100vh;
            background: linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000);
            background-size: 400% 400%;
            animation: gradientShift 15s ease infinite;
            color: white;
            font-family: 'Inter', sans-serif;
            position: relative;
            overflow-x: hidden;
          }
          
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          .dashboard-particles {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1;
          }
          
          .dashboard-particle {
            position: absolute;
            background: linear-gradient(180deg, 
              #800000 0%,    /* Maroon base */
              #b22222 15%,   /* Fire brick */
              #dc143c 30%,   /* Crimson */
              #ff4500 50%,   /* Orange-red */
              #ff6347 70%,   /* Tomato */
              #ff8c00 85%,   /* Dark orange */
              #ffa500 95%,   /* Orange */
              transparent 100%
            );
            border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
            animation: dashboardFire 7s linear infinite;
            filter: blur(0.6px);
            box-shadow: 0 0 6px rgba(255, 69, 0, 0.4);
            opacity: 0;
            visibility: hidden;
          }
          
          @keyframes dashboardFire {
            0% {
              transform: translateY(105vh) translateX(0px) scale(0.5) rotate(0deg);
              opacity: 0;
              visibility: hidden;
            }
            5% {
              opacity: 0.7;
              visibility: visible;
              transform: translateY(95vh) translateX(0.5px) scale(0.6) rotate(0.5deg);
            }
            15% {
              transform: translateY(85vh) translateX(1px) scale(0.7) rotate(1deg);
            }
            25% {
              transform: translateY(75vh) translateX(-1px) scale(0.9) rotate(-1deg);
            }
            40% {
              transform: translateY(60vh) translateX(2px) scale(1.0) rotate(2deg);
            }
            55% {
              transform: translateY(45vh) translateX(-1px) scale(1.1) rotate(-1deg);
            }
            70% {
              transform: translateY(30vh) translateX(1px) scale(0.9) rotate(1deg);
            }
            85% {
              opacity: 0.5;
              transform: translateY(15vh) translateX(0px) scale(0.7) rotate(0deg);
            }
            100% {
              transform: translateY(-5vh) translateX(0px) scale(0.3) rotate(0deg);
              opacity: 0;
              visibility: hidden;
            }
          }
          
          .dashboard-section {
            position: relative;
            z-index: 2;
            margin: 2rem 0;
            padding: 0 1rem;
          }
          
          .dashboard-header {
            text-align: center;
            font-size: 2.5rem;
            font-weight: 800;
            margin: 2rem 0;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: rainbowFlow 3s ease-in-out infinite;
            text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
          }
          
          .dashboard-subheader {
            text-align: center;
            font-size: 1.8rem;
            font-weight: 700;
            margin: 1.5rem 0;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: rainbowFlow 3s ease-in-out infinite;
          }
          
          @keyframes rainbowFlow {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          
          .glassmorphism-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 20px;
            padding: 2rem;
            margin: 1rem 0;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            transition: all 0.3s ease;
          }
          
          .glassmorphism-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.3);
          }
          
          .tile-container {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            justify-content: center;
            padding: 1rem;
          }
          
          .tile {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border-radius: 15px;
            padding: 1.5rem;
            transition: all 0.3s ease;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            position: relative;
            overflow: hidden;
            max-width: 350px;
            width: 100%;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .tile::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
            transition: left 0.5s ease;
            z-index: -1;
            pointer-events: none;
          }
          
          .tile:hover::before {
            left: 100%;
          }
          
          .tile:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
          }
          
          .tile-selected {
            animation: rainbowGlow 3s linear infinite;
          }
          
          @keyframes rainbowGlow {
            0% { 
              box-shadow: 0 0 20px #ff0000, 0 0 40px #ff0000, 0 0 60px #ff0000;
            }
            14% { 
              box-shadow: 0 0 20px #ff8c00, 0 0 40px #ff8c00, 0 0 60px #ff8c00;
            }
            28% { 
              box-shadow: 0 0 20px #ffd700, 0 0 40px #ffd700, 0 0 60px #ffd700;
            }
            42% { 
              box-shadow: 0 0 20px #32cd32, 0 0 40px #32cd32, 0 0 60px #32cd32;
            }
            57% { 
              box-shadow: 0 0 20px #00bfff, 0 0 40px #00bfff, 0 0 60px #00bfff;
            }
            71% { 
              box-shadow: 0 0 20px #8a2be2, 0 0 40px #8a2be2, 0 0 60px #8a2be2;
            }
            85% { 
              box-shadow: 0 0 20px #ff1493, 0 0 40px #ff1493, 0 0 60px #ff1493;
            }
            100% { 
              box-shadow: 0 0 20px #ff0000, 0 0 40px #ff0000, 0 0 60px #ff0000;
            }
          }
          
          .loading-spinner {
            text-align: center;
            font-size: 1.5rem;
            font-weight: 600;
            padding: 3rem;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: rainbowFlow 3s ease-in-out infinite, pulse 2s ease-in-out infinite;
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
          }
          
          .leaderboard-container {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 2rem;
            margin: 2rem auto;
            max-width: 1000px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          }
          
          .leaderboard-container table {
            width: 100%;
            border-collapse: collapse;
            background: transparent;
          }
          
          .leaderboard-container th {
            padding: 1rem 0.5rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-size: 0.9rem;
            position: relative;
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-bottom: 2px solid rgba(255, 255, 255, 0.3);
            color: white;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
            transition: all 0.3s ease;
          }
          
          .leaderboard-container th::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.1);
            opacity: 0;
            transition: opacity 0.3s ease;
            border-radius: 4px;
          }
          
          .leaderboard-container th:hover::before {
            opacity: 1;
          }
          
          .leaderboard-container th:hover {
            transform: translateY(-2px);
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.4);
            text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
          }
          
          .leaderboard-container td {
            padding: 0.8rem 0.5rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s ease;
          }
          
          .leaderboard-container tr:hover td {
            background: rgba(255, 255, 255, 0.1);
            transform: scale(1.01);
          }
          
          .deals-toggle {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 2rem;
            margin: 2rem 0;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border-radius: 50px;
            padding: 1rem 2rem;
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          .deals-toggle h2 {
            margin: 0;
            cursor: pointer;
            transition: all 0.3s ease;
            border-radius: 25px;
            font-size: 1.2rem;
          }
          
          .deals-toggle h2:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: scale(1.05);
          }
          
          .wallet-info {
            text-align: center;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 15px;
            padding: 1rem;
            margin: 2rem auto;
            max-width: 600px;
            transition: all 0.3s ease;
          }
          
          .wallet-info:hover {
            background: rgba(255, 255, 255, 0.15);
            transform: scale(1.02);
          }
          
          .upgrade-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            justify-content: center;
            padding: 1rem;
          }
          
          .upgrade-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 15px;
            padding: 1rem;
            max-width: 350px;
            width: 100%;
            word-wrap: break-word;
            overflow-wrap: break-word;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }
          
          .upgrade-card h3 {
            font-size: 1rem;
            margin: 0 0 0.5rem 0;
            font-weight: 600;
          }
          
          .upgrade-card h4 {
            font-size: 0.9rem;
            margin: 1rem 0 0.5rem 0;
            font-weight: 600;
          }
          
          .upgrade-card ul {
            margin: 0.5rem 0;
            padding: 0;
          }
          
          .upgrade-card li {
            margin-bottom: 0.5rem;
            font-size: 0.85rem;
          }
          
          .upgrade-card p {
            font-size: 0.8rem;
            margin: 0.5rem 0 0 0;
            opacity: 0.8;
          }
          
          .upgrade-card::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.05), transparent);
            transform: rotate(45deg);
            transition: all 0.3s ease;
            opacity: 0;
            z-index: -1;
            pointer-events: none;
          }
          
          .upgrade-card:hover::before {
            opacity: 1;
            animation: shimmer 1.5s ease infinite;
          }
          
          @keyframes shimmer {
            0% { transform: rotate(45deg) translate(-100%, -100%); }
            100% { transform: rotate(45deg) translate(100%, 100%); }
          }
          
          .upgrade-card:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.4);
          }
          
          .floating-button {
            position: fixed;
            bottom: 60px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1000;
            animation: float 3s ease-in-out infinite;
          }
          
          @keyframes float {
            0%, 100% { transform: translateX(-50%) translateY(0px); }
            50% { transform: translateX(-50%) translateY(-10px); }
          }
          
          .rainbowBtn {
            display: inline-block;
            padding: 1rem 2rem;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            border: none;
            border-radius: 50px;
            color: white;
            font-size: 1rem;
            font-weight: 600;
            text-decoration: none;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.3s ease;
            animation: rainbowFlow 3s ease-in-out infinite;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            cursor: pointer;
          }
          
          .rainbowBtn:hover {
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
          }
          
          .section-divider {
            height: 2px;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            margin: 3rem 0;
            border-radius: 1px;
          }
          
          .floating-action-buttons {
            position: fixed;
            right: 2rem;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            flex-direction: column;
            gap: 1rem;
            z-index: 10001;
          }
          
          .fab-button {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(15px);
            border: 3px solid rgba(255, 255, 255, 0.3);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            position: relative;
            overflow: hidden;
            animation: fabPulse 4s ease-in-out infinite;
            margin-bottom: 0.5rem;
          }
          
          .fab-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 1rem;
          }
          
          .fab-label {
            font-size: 0.7rem;
            font-weight: 600;
            color: white;
            text-transform: uppercase;
            letter-spacing: 1px;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
            margin-top: 0.3rem;
            opacity: 0.9;
          }
          
          @keyframes fabPulse {
            0%, 100% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(255, 0, 0, 0.7);
            }
            14% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(255, 140, 0, 0);
            }
            28% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(255, 215, 0, 0.7);
            }
            42% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(50, 205, 50, 0);
            }
            57% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(0, 191, 255, 0.7);
            }
            71% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(138, 43, 226, 0);
            }
            85% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(255, 20, 147, 0.7);
            }
          }
          
          .fab-button::before {
            content: '';
            position: absolute;
            top: -3px;
            left: -3px;
            right: -3px;
            bottom: -3px;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            animation: rainbowFlow 2s ease-in-out infinite;
            opacity: 0.8;
            border-radius: 50%;
            z-index: -1;
          }
          
          .fab-button:hover::before {
            opacity: 1;
            animation: rainbowFlow 1s ease-in-out infinite;
          }
          
          .fab-button:hover {
            transform: scale(1.15);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.6);
          }
          
          .fab-button span {
            position: relative;
            z-index: 2;
          }
          
          .slide-out-panel {
            position: fixed;
            top: 0;
            right: 0;
            height: 100vh;
            background: rgba(0, 0, 0, 0.95);
            backdrop-filter: blur(25px);
            border-left: 2px solid rgba(255, 255, 255, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            z-index: 10002;
            overflow-y: auto;
            padding: 2rem;
          }
          

          
          .deals-panel {
            width: 520px;
          }
          
          .faucet-panel {
            width: 520px;
          }
          
          .leaderboard-panel {
            width: 1020px;
          }
          
          .slide-out-panel.open {
            transform: translateX(0);
          }
          
          .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid rgba(255, 255, 255, 0.2);
          }
          
          .panel-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: white;
          }
          
          .panel-close {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 1.2rem;
          }
          
          .panel-close:hover {
            background: rgba(255, 0, 0, 0.2);
            border-color: rgba(255, 0, 0, 0.5);
            transform: scale(1.1);
          }
          
          .panel-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.3);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            z-index: 10001;
          }
          
          .panel-overlay.open {
            opacity: 1;
            visibility: visible;
          }
          
          @media (max-width: 768px) {
            .slide-out-panel {
              width: 100%;
            }
            .floating-action-buttons {
              right: 1rem;
            }
          }
          
          .music-controls {
            position: fixed;
            bottom: 2rem;
            left: 2rem;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            z-index: 1000;
          }
          
          .music-toggle {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(15px);
            border: 3px solid rgba(255, 255, 255, 0.3);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            animation: musicPulse 3s ease-in-out infinite;
          }
          
          .volume-controls {
            display: flex;
            gap: 0.25rem;
            justify-content: center;
          }
          
          .volume-btn {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            border: 2px solid rgba(255, 255, 255, 0.2);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
            font-weight: bold;
          }
          
          .volume-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.4);
            transform: scale(1.1);
          }
          
          .volume-btn:disabled {
            opacity: 0.3;
            cursor: not-allowed;
            transform: none;
          }
          
          .volume-display {
            text-align: center;
            font-size: 0.7rem;
            color: white;
            opacity: 0.8;
            font-weight: 600;
            margin-top: 0.25rem;
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
          }
          
          @keyframes musicPulse {
            0%, 100% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 0 rgba(138, 43, 226, 0.7);
            }
            50% { 
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(138, 43, 226, 0);
            }
          }
          
          .music-toggle::before {
            content: '';
            position: absolute;
            top: -3px;
            left: -3px;
            right: -3px;
            bottom: -3px;
            background: linear-gradient(45deg, #8a2be2, #ff1493, #00bfff, #32cd32);
            background-size: 400% 400%;
            animation: rainbowFlow 2s ease-in-out infinite;
            opacity: 0.8;
            border-radius: 50%;
            z-index: -1;
          }
          
          .music-toggle:hover::before {
            opacity: 1;
            animation: rainbowFlow 1s ease-in-out infinite;
          }
          
          .music-toggle:hover {
            transform: scale(1.1);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.6);
          }
          
          .music-toggle.muted {
            opacity: 0.5;
            animation: none;
          }
          
          @media (max-width: 768px) {
            .music-controls {
              bottom: 1rem;
              left: 1rem;
            }
            .music-toggle {
              width: 50px;
              height: 50px;
            }
            .music-toggle svg {
              width: 20px;
              height: 20px;
            }
            .volume-btn {
              width: 24px;
              height: 24px;
            }
            .volume-btn svg {
              width: 12px;
              height: 12px;
            }
            .volume-display {
              font-size: 0.6rem;
            }
          }
        `}
      </style>

      <div className="dashboard-particles" id="dashboard-particles"></div>

      {/* Token Balances Display - Top Left */}
      {wallet && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px',
          padding: '1rem',
          zIndex: 10001,
          minWidth: '220px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '0.9rem',
              fontWeight: '600',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              💰 Token Balances
            </h3>
            <button
              onClick={() => fetchTokenBalances(wallet)}
              disabled={balancesLoading}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                cursor: balancesLoading ? 'wait' : 'pointer',
                transition: 'all 0.2s'
              }}
              title="Refresh balances"
            >
              {balancesLoading ? '⏳' : '🔄'}
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* Polygon MATIC */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem',
              background: 'rgba(130, 71, 229, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(130, 71, 229, 0.3)'
            }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '500' }}>
                MATIC
              </span>
              <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>
                {tokenBalances.polygonMatic}
              </span>
            </div>

            {/* Polygon MANA */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem',
              background: 'rgba(255, 87, 51, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 87, 51, 0.3)'
            }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '500' }}>
                MANA
              </span>
              <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>
                {tokenBalances.polygonMana}
              </span>
            </div>

            {/* Monad Native */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem',
              background: 'rgba(0, 245, 255, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(0, 245, 255, 0.3)'
            }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '500' }}>
                MON
              </span>
              <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>
                {tokenBalances.monadNative}
              </span>
            </div>

            {/* Monad DRIP */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.4rem',
              background: 'rgba(255, 0, 255, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 0, 255, 0.3)'
            }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '500' }}>
                DRIP
              </span>
              <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>
                {tokenBalances.monadDrip}
              </span>
            </div>

            {/* Genesis Skulls */}
            <div 
              onClick={() => window.open('https://nadscriptions.com', '_blank')}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.4rem',
                background: 'rgba(139, 69, 19, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 69, 19, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(139, 69, 19, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(139, 69, 19, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(139, 69, 19, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(139, 69, 19, 0.3)';
              }}
            >
              <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '500' }}>
                GENESIS SKULLS
              </span>
              <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>
                {tokenBalances.genesisSkullsCount}
              </span>
            </div>
          </div>
        </div>
      )}

      {wallet && (
        <button
          onClick={onDisconnectWallet}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(45deg, #8B0000, #DC143C, #FF4500, #FF6347)',
            backgroundSize: '300% 300%',
            animation: 'redGradient 3s ease infinite',
            border: 'none',
            borderRadius: '25px',
            color: 'white',
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            zIndex: 10001,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 4px 15px rgba(139, 0, 0, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(139, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(139, 0, 0, 0.3)';
          }}
        >
          Disconnect
        </button>
      )}

      <style>
        {wallet && `
                      @keyframes redGradient {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            @keyframes faucetGlow {
              0%, 100% { 
                background-position: 0% 50%;
                box-shadow: 0 0 20px rgba(0, 191, 255, 0.5);
              }
              50% { 
                background-position: 100% 50%;
                box-shadow: 0 0 30px rgba(138, 43, 226, 0.8);
              }
            }
        `}
      </style>

      <div className="dashboard-section">
        <h2 className="dashboard-header">NFT Dashboard</h2>
      </div>

      {/* Floating Action Buttons */}
      <div className="floating-action-buttons">
        <div className="fab-container">
          <div 
            className="fab-button" 
            onClick={() => setShowDealsPanel(true)}
            title="View Deals"
          >
            <span style={{ color: 'white', fontWeight: 'bold' }}>$</span>
          </div>
          <div className="fab-label">DEALS</div>
        </div>
        <div className="fab-container">
          <div 
            className="fab-button" 
            onClick={() => setShowLeaderboardPanel(true)}
            title="View Leaderboard"
          >
            <span style={{ color: 'white', fontWeight: 'bold' }}>♔</span>
          </div>
          <div className="fab-label">RANKS</div>
        </div>
        <div className="fab-container">
          <div 
            className="fab-button" 
            onClick={() => setShowLookupPanel(true)}
            title="Claim Lookup"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="fab-label">LOOKUP</div>
        </div>
        <div className="fab-container">
          <div 
            className="fab-button" 
            onClick={() => setShowFaucetPanel(true)}
            title="POL Faucet"
            style={{
              background: faucetConfig?.enabled && faucetStatus?.canClaim 
                ? 'linear-gradient(45deg, #00bfff, #8a2be2, #00bfff)' 
                : 'linear-gradient(45deg, #666, #888, #666)',
              animation: faucetConfig?.enabled && faucetStatus?.canClaim 
                ? 'fabPulse 4s ease-in-out infinite, faucetGlow 2s ease-in-out infinite' 
                : 'fabPulse 4s ease-in-out infinite'
            }}
          >
            <span style={{ color: 'white', fontWeight: 'bold' }}>☂</span>
          </div>
          <div className="fab-label">FAUCET</div>
        </div>
      </div>

      {/* Panel Overlays */}
      <div
        className={`panel-overlay ${showDealsPanel ? 'open' : ''}`}
        onClick={() => setShowDealsPanel(false)}
      ></div>
      <div
        className={`panel-overlay ${showLeaderboardPanel ? 'open' : ''}`}
        onClick={() => setShowLeaderboardPanel(false)}
      ></div>
      <div
        className={`panel-overlay ${showLookupPanel ? 'open' : ''}`}
        onClick={() => setShowLookupPanel(false)}
      ></div>
      <div
        className={`panel-overlay ${showFaucetPanel ? 'open' : ''}`}
        onClick={() => setShowFaucetPanel(false)}
      ></div>

      {/* Deals Panel */}
      <div className={`slide-out-panel deals-panel ${showDealsPanel ? 'open' : ''}`}>
        <div className="panel-header">
          <div className="panel-title">🔖 Token-Gated Deals & Discounts</div>
          <div className="panel-close" onClick={() => setShowDealsPanel(false)}>
            ×
          </div>
        </div>
        <div>
          {!loading && !dealsLoading && (
            <div className="deals-toggle">
              <h2
                onClick={() => setShowLocked(false)}
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  color: showLocked ? '#888' : '#FFF',
                  margin: 0,
                }}
              >
                🔖 Unlocked Deals
              </h2>
              <span style={{ margin: '0 0.5rem' }}>|</span>
              <h2
                onClick={() => setShowLocked(true)}
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  color: showLocked ? '#FFF' : '#888',
                  margin: 0,
                }}
              >
                🔒 Locked Deals
              </h2>
            </div>
          )}
          <DealsSection
            dealsLoading={dealsLoading}
            loading={loading}
            showLocked={showLocked}
            lockedDeals={lockedDeals}
            availableDeals={availableDeals}
            walletAddress={wallet}
            selectedWalletType={selectedWalletType}
            ownedNFTs={nfts}
            onError={(errorConfig) => {
              setErrorModalConfig(errorConfig);
              setShowErrorModal(true);
            }}
          />
        </div>
      </div>

      {/* Leaderboard Panel */}
      <div className={`slide-out-panel leaderboard-panel ${showLeaderboardPanel ? 'open' : ''}`}>
        <div className="panel-header">
          <div className="panel-title">🏆 Leaderboard</div>
          <div className="panel-close" onClick={() => setShowLeaderboardPanel(false)}>
            ×
          </div>
        </div>
        <div>
          <div className="leaderboard-container" style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              zIndex: 10
            }}>
              <label style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                Sort by: {' '}
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  style={{
                    padding: '.3rem .5rem',
                    borderRadius: '.3rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                >
                  <option value="daily" style={{ background: '#333', color: 'white' }}>NFT Ranking</option>
                  <option value="total" style={{ background: '#333', color: 'white' }}>Stunt Miles</option>
                </select>
              </label>
            </div>

            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '.5rem' }}>Rank</th>
                  <th style={{ textAlign: 'left', padding: '.5rem' }}>Wallet</th>
                  <th style={{ textAlign: 'right', padding: '.5rem' }}>Items</th>
                  <th style={{ textAlign: 'right', padding: '.5rem' }}>Upgraded</th>
                  <th style={{ textAlign: 'right', padding: '.5rem' }}>Rarity Score</th>
                  <th style={{ textAlign: 'right', padding: '.5rem' }}>Daily Stunt Miles</th>
                  <th style={{ textAlign: 'right', padding: '.5rem' }}>Total Stunt Miles</th>
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((u, i) => (
                  <tr key={u.wallet}>
                    <td style={{ padding: '.5rem' }}>{i + 1}</td>
                    <td style={{ padding: '.5rem', fontFamily: 'monospace' }}>
                      {u.wallet.slice(0, 6)}…{u.wallet.slice(-4)}
                    </td>
                    <td style={{ padding: '.5rem', textAlign: 'right' }}>{u.item_count}</td>
                    <td style={{ padding: '.5rem', textAlign: 'right' }}>{u.upgraded_count}</td>
                    <td style={{ padding: '.5rem', textAlign: 'right' }}>{u.rarity_score}</td>
                    <td style={{ padding: '.5rem', textAlign: 'right' }}>{u.daily_points || 0}</td>
                    <td style={{ padding: '.5rem', textAlign: 'right' }}>{u.total_points || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Faucet Panel */}
      <div className={`slide-out-panel faucet-panel ${showFaucetPanel ? 'open' : ''}`}>
        <div className="panel-header">
          <div className="panel-title">🚰 POL Faucet</div>
          <div className="panel-close" onClick={() => setShowFaucetPanel(false)}>
            ×
          </div>
        </div>
        <div style={{ padding: '1rem' }}>
          {faucetConfig && faucetConfig.enabled ? (
            <>
              {/* Check if user has access */}
              {(() => {
                const hasAccess = checkTokenGateAccess(
                  faucetConfig.token_gate_requirements || [],
                  faucetConfig.token_gate_urn_requirements || [],
                  faucetConfig.token_gate_logic || 'AND',
                  nfts
                );

                if (hasAccess) {
                  return (
                    <>
                      <div style={{
                        textAlign: 'center',
                        padding: '1.5rem',
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#22c55e', marginBottom: '0.5rem' }}>
                          ✅ VIP Access Granted
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '1rem' }}>
                          You have access to our exclusive POL faucet
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#00bfff', marginBottom: '0.5rem' }}>
                          {faucetConfig.amount_pol} POL Tokens Available
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                          Perfect for 2-3 transactions on Polygon
                        </div>
                      </div>

                      {faucetStatus?.canClaim ? (
                        <button
                          className="rainbowBtn"
                          onClick={() => setShowFaucetModal(true)}
                          style={{ 
                            width: '100%', 
                            fontSize: '1.1rem', 
                            padding: '1rem',
                            background: 'linear-gradient(45deg, #00bfff, #8a2be2, #00bfff)',
                            animation: 'faucetGlow 2s ease-in-out infinite'
                          }}
                        >
                          💧 Claim POL Tokens
                        </button>
                      ) : (
                        <div style={{
                          padding: '1rem',
                          background: 'rgba(255, 193, 7, 0.1)',
                          border: '1px solid rgba(255, 193, 7, 0.3)',
                          borderRadius: '0.5rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ color: '#ffc107', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            ⏰ Cooldown Active
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#888' }}>
                            {faucetStatus?.reason || 'Please wait before claiming again'}
                          </div>
                        </div>
                      )}
                    </>
                  );
                } else {
                  return (
                    <div style={{
                      padding: '1.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        🔒 VIP Access Required
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '1rem' }}>
                        This faucet is exclusive to our VIP NFT holders. You need to own one of our special collections to access it.
                      </div>
                      
                      {faucetConfig.token_gate_requirements && faucetConfig.token_gate_requirements.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            Required Collections ({faucetConfig.token_gate_logic}):
                          </div>
                          {faucetConfig.token_gate_requirements.map((req, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.5rem',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '0.25rem',
                              marginBottom: '0.5rem'
                            }}>
                              <div style={{ textAlign: 'left' }}>
                                <div style={{ fontWeight: 'bold' }}>{req.name}</div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                                  {req.quantity} NFT{req.quantity > 1 ? 's' : ''} required
                                </div>
                              </div>
                              {req.marketplace_link && (
                                <a
                                  href={req.marketplace_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    background: '#8a2be2',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  View
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
              })()}
            </>
          ) : (
            <div style={{
              padding: '1.5rem',
              background: 'rgba(107, 114, 128, 0.1)',
              border: '1px solid rgba(107, 114, 128, 0.3)',
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}>
              <div style={{ color: '#6b7280', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                🚰 Faucet Disabled
              </div>
              <div style={{ fontSize: '0.9rem', color: '#888' }}>
                The POL faucet is currently disabled. Check back later!
              </div>
            </div>
          )}

          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(138, 43, 226, 0.1)',
            border: '1px solid rgba(138, 43, 226, 0.3)',
            borderRadius: '0.5rem'
          }}>
            <div style={{ fontSize: '0.9rem', color: '#888', textAlign: 'left' }}>
              <strong style={{ color: '#8a2be2' }}>What is a faucet?</strong><br />
              A crypto faucet gives you a small amount of tokens for free. Our VIP faucet helps our NFT holders get POL tokens when they need a little boost to use our platform or make transactions on Polygon. It's our way of showing love to our community! 💜
            </div>
          </div>
        </div>
      </div>

      {/* UPGRADE MENU SECTION */}
      {!loading && (
        <div className="dashboard-section">
          <h2 className="dashboard-subheader">✨ Upgrade Menu</h2>
          {upgrades.length === 0 ? (
            <div className="glassmorphism-card">
              <p style={{ textAlign: 'center', margin: 0 }}>No upgrades available right now.</p>
            </div>
          ) : (
            <div className="upgrade-grid">
              {upgrades.map((u) => (
                <div
                  key={`${u.type}-${u.id}`}
                  className="upgrade-card"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <div style={{ flex: '1' }}>
                    <h3>{u.type === 'upgrade' ? `Upgrade ${u.title}` : u.title}</h3>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    {/* Main type flag */}
                    <span
                      style={{
                        backgroundColor: u.type === 'upgrade' ? 'green' : 
                          u.recipe_type === 'free_mint' ? '#007bff' : 'orange',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                      }}
                    >
                      {u.type === 'upgrade' ? 'Single Upgrade' : 
                        u.recipe_type === 'free_mint' ? 'Free Mint' : 'Multi-Item Recipe'}
                    </span>

                    {/* Token-gated flag */}
                    {(u.recipe_type === 'token_gated' || u.recipe_type === 'free_mint') && (
                      <span
                        style={{
                          backgroundColor: '#8a2be2',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                        }}
                      >
                        TOKEN-GATED
                      </span>
                    )}

                    {/* RNG flag */}
                    {u.rng_enabled && (
                      <span
                        style={{
                          backgroundColor: '#ff6b6b',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                        }}
                      >
                        🎲 RNG
                      </span>
                    )}

                    {/* Access status for token-gated recipes */}
                    {(u.recipe_type === 'token_gated' || u.recipe_type === 'free_mint') && (
                      <span
                        style={{
                          backgroundColor: checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts) ? '#28a745' : '#dc3545',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                        }}
                      >
                        {checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts) ? '✓ ACCESS' : '✗ LOCKED'}
                      </span>
                    )}
                  </div>

                  {/* Rewards section */}
                  <h4 style={{ marginTop: '1rem' }}>Rewards</h4>
                  {(() => {
                    const rewards = u.type === 'recipe' ? u.rewards : u.upgrade_options || [];
                    const isRNG = u.type === 'recipe' && u.rng_enabled;
                    const shouldUseGrid = isRNG && rewards.length === 4;
                    
                    if (shouldUseGrid) {
                      // 2x2 grid layout for 4 RNG rewards
                      return (
                        <div>
                          {/* Top row */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            marginBottom: '1rem'
                          }}>
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flex: '1',
                              maxWidth: '140px'
                            }}>
                              {rewards[0].image || rewards[0].targetImage ? (
                                <img
                                  src={rewards[0].image || rewards[0].targetImage}
                                  alt={rewards[0].upgradedItem || rewards[0].name || rewards[0].targetName}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'contain',
                                    marginBottom: '0.5rem',
                                  }}
                                />
                              ) : null}
                              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center' }}>
                                {rewards[0].quantity} × {rewards[0].upgradedItem || rewards[0].name || rewards[0].targetName}
                              </div>
                              {rewards[0].chance && (
                                <div style={{
                                  display: 'inline-block',
                                  background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                                  backgroundSize: '400% 400%',
                                  animation: 'rainbowFlow 3s ease-in-out infinite',
                                  color: '#fff',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '0.8rem',
                                  fontSize: '0.7rem',
                                  marginTop: '0.3rem',
                                  fontWeight: 'bold'
                                }}>
                                  🎲 {rewards[0].chance}%
                                </div>
                              )}
                            </div>
                            
                            <div style={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                              margin: '0 1rem'
                            }}>
                              OR
                            </div>
                            
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flex: '1',
                              maxWidth: '140px'
                            }}>
                              {rewards[1].image || rewards[1].targetImage ? (
                                <img
                                  src={rewards[1].image || rewards[1].targetImage}
                                  alt={rewards[1].upgradedItem || rewards[1].name || rewards[1].targetName}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'contain',
                                    marginBottom: '0.5rem',
                                  }}
                                />
                              ) : null}
                              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center' }}>
                                {rewards[1].quantity} × {rewards[1].upgradedItem || rewards[1].name || rewards[1].targetName}
                              </div>
                              {rewards[1].chance && (
                                <div style={{
                                  display: 'inline-block',
                                  background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                                  backgroundSize: '400% 400%',
                                  animation: 'rainbowFlow 3s ease-in-out infinite',
                                  color: '#fff',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '0.8rem',
                                  fontSize: '0.7rem',
                                  marginTop: '0.3rem',
                                  fontWeight: 'bold'
                                }}>
                                  🎲 {rewards[1].chance}%
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* ORs above bottom row */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            margin: '1rem 0 0.5rem 0'
                          }}>
                            <div style={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                              flex: '1',
                              textAlign: 'center',
                              maxWidth: '140px'
                            }}>
                              OR
                            </div>
                            
                            <div style={{ margin: '0 1rem' }}></div>
                            
                            <div style={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                              flex: '1',
                              textAlign: 'center',
                              maxWidth: '140px'
                            }}>
                              OR
                            </div>
                          </div>
                          
                          {/* Bottom row */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center'
                          }}>
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flex: '1',
                              maxWidth: '140px'
                            }}>
                              {rewards[2].image || rewards[2].targetImage ? (
                                <img
                                  src={rewards[2].image || rewards[2].targetImage}
                                  alt={rewards[2].upgradedItem || rewards[2].name || rewards[2].targetName}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'contain',
                                    marginBottom: '0.5rem',
                                  }}
                                />
                              ) : null}
                              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center' }}>
                                {rewards[2].quantity} × {rewards[2].upgradedItem || rewards[2].name || rewards[2].targetName}
                              </div>
                              {rewards[2].chance && (
                                <div style={{
                                  display: 'inline-block',
                                  background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                                  backgroundSize: '400% 400%',
                                  animation: 'rainbowFlow 3s ease-in-out infinite',
                                  color: '#fff',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '0.8rem',
                                  fontSize: '0.7rem',
                                  marginTop: '0.3rem',
                                  fontWeight: 'bold'
                                }}>
                                  🎲 {rewards[2].chance}%
                                </div>
                              )}
                            </div>
                            
                            <div style={{
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                              margin: '0 1rem'
                            }}>
                              OR
                            </div>
                            
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flex: '1',
                              maxWidth: '140px'
                            }}>
                              {rewards[3].image || rewards[3].targetImage ? (
                                <img
                                  src={rewards[3].image || rewards[3].targetImage}
                                  alt={rewards[3].upgradedItem || rewards[3].name || rewards[3].targetName}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'contain',
                                    marginBottom: '0.5rem',
                                  }}
                                />
                              ) : null}
                              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', textAlign: 'center' }}>
                                {rewards[3].quantity} × {rewards[3].upgradedItem || rewards[3].name || rewards[3].targetName}
                              </div>
                              {rewards[3].chance && (
                                <div style={{
                                  display: 'inline-block',
                                  background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                                  backgroundSize: '400% 400%',
                                  animation: 'rainbowFlow 3s ease-in-out infinite',
                                  color: '#fff',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '0.8rem',
                                  fontSize: '0.7rem',
                                  marginTop: '0.3rem',
                                  fontWeight: 'bold'
                                }}>
                                  🎲 {rewards[3].chance}%
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      // Original single column layout for other cases
                      return (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          textAlign: 'center'
                        }}>
                          {rewards.map((reward, i) => (
                            <React.Fragment key={i}>
                              <div style={{ 
                                marginBottom: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                minWidth: '120px'
                              }}>
                                {reward.image || reward.targetImage ? (
                                  <img
                                    src={reward.image || reward.targetImage}
                                    alt={reward.upgradedItem || reward.name || reward.targetName}
                                    style={{
                                      width: '140px',
                                      height: '140px',
                                      objectFit: 'contain',
                                      marginBottom: '0.5rem',
                                    }}
                                  />
                                ) : null}
                                <div style={{ fontWeight: 'bold' }}>
                                  {reward.quantity} ×{' '}
                                  {reward.upgradedItem || reward.name || reward.targetName}
                                </div>
                                {/* Show chance for RNG enabled recipes */}
                                {isRNG && reward.chance && (
                                  <div style={{
                                    display: 'inline-block',
                                    background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
                                    backgroundSize: '400% 400%',
                                    animation: 'rainbowFlow 3s ease-in-out infinite',
                                    color: '#fff',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontSize: '0.8rem',
                                    marginTop: '0.5rem',
                                    cursor: 'help',
                                    position: 'relative',
                                    fontWeight: 'bold'
                                  }}
                                  title={`${reward.chance}% chance to receive this reward`}>
                                    🎲 {reward.chance}% Chance
                                  </div>
                                )}
                              </div>
                              {isRNG && i < rewards.length - 1 && (
                                <div style={{
                                  fontSize: '1.2rem',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                  margin: '0.5rem 0',
                                  width: '100%',
                                  textAlign: 'center'
                                }}>
                                  OR
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    }
                  })()}
                  </div>

                  {/* Bottom section - Access Requirements and Buttons */}
                  <div style={{ marginTop: 'auto' }}>
                  {/* Token Gate Requirements section */}
                  {(u.recipe_type === 'token_gated' || u.recipe_type === 'free_mint') && u.token_gate_requirements?.length > 0 && (
                    <>
                      <h4>Access Requirements ({u.token_gate_logic || 'AND'})</h4>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        {u.token_gate_requirements.map((gate, i) => {
                          const hasAccess = checkTokenGateAccess([gate], u.token_gate_urn_requirements ? [u.token_gate_urn_requirements[i]] : [], 'AND', nfts);
                          // Get proper collection item info (name and image)
                          // For URN-based token gates, use urn_item_id for display, not item_id
                          const urnRequirement = u.token_gate_urn_requirements ? u.token_gate_urn_requirements[i] : null;
                          const displayItemId = urnRequirement?.urn_item_id || gate.item_id;
                          
                          const itemInfo = getCollectionItemInfo(collectionItems, gate.collection, displayItemId);
                          
                          const collectionInfo = {
                            name: itemInfo.name,
                            image: itemInfo.image || gate.image || null,
                            marketplace_link: gate.marketplace_link || null
                          };
                          
                          
                          return (
                            <li key={i} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: '0.5rem',
                              padding: '0.5rem',
                              backgroundColor: hasAccess ? 'rgba(40, 167, 69, 0.2)' : 'rgba(220, 53, 69, 0.2)',
                              borderRadius: '4px'
                            }}>
                              <span style={{ marginRight: '0.5rem' }}>
                                {hasAccess ? '✓' : '✗'}
                              </span>
                              {collectionInfo.image && (
                                <img
                                  src={collectionInfo.image}
                                  alt={collectionInfo.name}
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    objectFit: 'contain',
                                    marginRight: '0.5rem',
                                    borderRadius: '4px'
                                  }}
                                />
                              )}
                              <span 
                                style={{ 
                                  cursor: collectionInfo.marketplace_link ? 'pointer' : 'default',
                                  textDecoration: collectionInfo.marketplace_link ? 'underline' : 'none'
                                }}
                                onClick={() => {
                                  if (collectionInfo.marketplace_link) {
                                    window.open(collectionInfo.marketplace_link, '_blank');
                                  }
                                }}
                              >
                                {gate.quantity} × {collectionInfo.name}
                                {gate.item_id && ` (ID: ${gate.item_id})`}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <p style={{ fontSize: '0.85rem', fontStyle: 'italic', marginBottom: '1rem' }}>
                        {u.token_gate_logic === 'OR' 
                          ? 'You must own ANY ONE of the above collections to access this recipe'
                          : 'You must own ALL of the above collections to access this recipe'
                        }
                      </p>
                      
                      {/* FREE MINT button for free mint recipes */}
                      {u.recipe_type === 'free_mint' && (
                        <>
                          <button
                            onClick={async () => {
                              const eligibility = await checkRecipeEligibility(u, nfts);
                              if (eligibility.canClaim) {
                                setRecipeToCraft(u);
                                setShowRecipeModal(true);
                              } else {
                                showUserFriendlyError(
                                  { message: eligibility.reason },
                                  eligibility.type
                                );
                              }
                            }}
                            disabled={!checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts) || (tokenAvailability[u.id] === false)}
                            style={{
                              padding: '0.75rem 1.5rem',
                              borderRadius: '25px',
                              border: 'none',
                              background: !checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts)
                                ? '#6c757d' // Locked (no access)
                                : tokenAvailability[u.id] === false
                                  ? '#dc3545' // Already claimed (has access but no tokens available)
                                  : 'linear-gradient(45deg, #007bff, #0056b3)', // Available
                              color: 'white',
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              cursor: (checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts) && tokenAvailability[u.id] !== false) ? 'pointer' : 'not-allowed',
                              transition: 'all 0.3s ease',
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              width: '100%',
                              marginTop: '0.5rem'
                            }}
                          >
                            {!checkTokenGateAccess(u.token_gate_requirements, u.token_gate_urn_requirements, u.token_gate_logic, nfts)
                              ? '🔒 FREE CLAIM LOCKED'
                              : tokenAvailability[u.id] === false
                                ? '✅ ALREADY CLAIMED'
                                : '🎁 FREE MINT'
                            }
                          </button>
                          
                          {/* Claim statistics display */}
                          {claimStatistics[u.id] && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#888',
                              marginTop: '0.5rem',
                              textAlign: 'center',
                              lineHeight: '1.3'
                            }}>
                            <div>Claims per Wallet: {claimStatistics[u.id].walletUsed}/{claimStatistics[u.id].walletLimit}</div>
                            {claimStatistics[u.id].perTokenEnabled && claimStatistics[u.id].tokenCount > 0 && (
                              <div>
                                Claims per Token: {claimStatistics[u.id].totalTokenUsed}/{claimStatistics[u.id].totalTokenLimit}
                                {claimStatistics[u.id].tokenCount > 1 && (
                                  <span style={{ color: '#aaa', fontSize: '0.9em' }}>
                                    {' '}({claimStatistics[u.id].tokenCount} tokens × {claimStatistics[u.id].perTokenLimit} each)
                                  </span>
                                )}
                              </div>
                            )}
                            </div>
                          )}

                          {/* MINT button for linked deals - only show when mint launching is inactive */}
                          {!mintLaunchingMode && (() => {
                            const linkedDeals = getLinkedDealsForRecipe(u.id);
                            if (linkedDeals.length === 0) return null;
                            
                            // Get the first linked deal (assuming one deal per recipe for now)
                            const deal = linkedDeals[0];
                            
                            // Skip if deal is not a DCL wearable deal
                            if (deal.deal_type !== 'dcl_wearable') {
                              return null;
                            }
                            
                            // Calculate price from deal_items or fallback to single item
                            const dealItems = deal.deal_items && deal.deal_items.length > 0 ? deal.deal_items : 
                              [{ dcl_price_mana: deal.dcl_price_mana }];
                            
                            // If RNG is enabled, only charge for one item
                            const bundlePrice = deal.rng_enabled ? 
                              parseFloat(dealItems[0].dcl_price_mana) : 
                              dealItems.reduce((sum, item) => sum + (parseFloat(item.dcl_price_mana) || 0), 0);
                            
                            const priceText = bundlePrice > 0 ? `${bundlePrice} MANA` : 'Free Mint';
                            
                            return (
                              <button
                                onClick={() => handleDCLPurchaseFromMintButton(deal)}
                                style={{
                                  padding: '0.75rem 1.5rem',
                                  borderRadius: '25px',
                                  border: 'none',
                                  background: 'linear-gradient(45deg, #8a2be2, #6a1b9a)',
                                  color: 'white',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  width: '100%',
                                  marginTop: '0.5rem'
                                }}
                              >
                                MINT - {priceText}
                              </button>
                            );
                          })()}
                        </>
                      )}

                    </>
                  )}

                  {/* Burn Requirements section - only for recipes that require burning */}
                  {u.recipe_type !== 'free_mint' && (
                    <>
                      <h4>
                        Burn Requirements
                        {u.type === 'recipe' ? ` (${u.logic || 'AND'})` : ''}
                      </h4>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
         {(() => {
                          const requirements = (u.type === 'recipe'
                            ? resolveBurnRequirements(u.requires, u.burn_urn_requirements)
                            : u.requirement
                              ? [u.requirement]
                              : []
                          );
                          
                          
                          return requirements;
                        })().map((req, i) => (
                          <li key={i}>
                            {req.image && (
                              <img
                                src={req.image}
                                alt={req.name}
                                style={{
                                  width: '30px',
                                  marginRight: '0.5rem',
                                  verticalAlign: 'middle',
                                }}
                              />
                            )}
                            {req.quantity} × {req.name}
                          </li>
                        ))}
                      </ul>
                      <p>
                        {u.type === 'recipe' && u.logic === 'OR' 
                          ? 'To craft, select ANY ONE of the required NFTs below'
                          : u.recipe_type === 'free_mint'
                            ? 'No NFTs need to be burned for this free mint'
                            : 'To craft, select all required NFTs below'
                        }
                      </p>
                    </>
                  )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* NFT GRID */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '1.5rem' }}>
          <img
            src="/dripp-nft-spin-540px.gif"
            alt="Loading NFTs, deals & discounts"
            style={{ 
              width: '300px', 
              height: 'auto', 
              display: 'block', 
              margin: '0 auto 0 auto' 
            }}
          />
          <div className="loading-spinner">LOADING NFTS, DEALS & DISCOUNTS...</div>
        </div>
      ) : (
        <div className="dashboard-section">
          <h2 className="dashboard-subheader">🧢 Your Collection</h2>
          <div className="tile-container">
            {nfts.length > 0 ? (
              nfts.map((nft) => {
                const isSelected = selectedNFTs.some(
                  (item) =>
                    item.tokenId.toString() === nft.tokenId.toString() &&
                    item.collection.toLowerCase() === nft.collection.toLowerCase()
                );
                return (
                  <div
                    key={`${nft.collection}-${nft.tokenId}`}
                    className={`tile ${isSelected ? 'tile-selected' : ''}`}
                    style={{
                      border: isSelected
                        ? '2px solid orange'
                        : nft.isMultiUpgradeable
                          ? '2px solid orange'
                          : nft.upgradeable
                            ? '2px solid limegreen'
                            : '1px solid #ccc',
                      cursor: nft.upgradeable || nft.isMultiUpgradeable ? 'pointer' : 'default'
                    }}
                    onClick={() =>
                      (nft.upgradeable || nft.isMultiUpgradeable) && toggleNFTSelection(nft)
                    }
                  >
                    <div style={{ position: 'relative' }}>
                      {nft.upgradeable && !nft.isMultiUpgradeable && (
                        <div
                          style={{
                            position: 'absolute',
                            top: -30,
                            left: 0,
                            background: 'limegreen',
                            color: '#fff',
                            padding: '0.25rem 0.5rem',
                            borderTopLeftRadius: '0.5rem',
                            borderBottomRightRadius: '0.5rem',
                            fontSize: '0.8rem'
                          }}
                        >
                          UPGRADEABLE
                        </div>
                      )}
                      {nft.isMultiUpgradeable && (
                        <div
                          style={{
                            position: 'absolute',
                            top: -30,
                            left: 0,
                            background: 'orange',
                            color: '#fff',
                            padding: '0.25rem 0.5rem',
                            borderTopLeftRadius: '0.5rem',
                            borderBottomRightRadius: '0.5rem',
                            fontSize: '0.8rem'
                          }}
                        >
                          MULTI-UPGRADE
                        </div>
                      )}

                      <h2 style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{nft.title}</h2>
                    </div>
                    <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <strong style={{ fontSize: '1.6em', color: 'teal' }}>Token ID: {nft.tokenId}</strong>
                    </p>

                    <img src={nft.image} alt={nft.title} style={{ maxWidth: '150px' }} />
                    <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <strong>Collection:</strong> {nft.collection}
                    </p>
                  </div>
                );
              })
            ) : (
              <div className="glassmorphism-card">
                <p style={{ textAlign: 'center', margin: 0 }}>You don't have any NFTs.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="wallet-info">
        <p style={{ margin: 0, userSelect: 'none' }}>
          {loading ? (
            <>
              Loading NFTs for wallet: {' '}
              <span
                onClick={copyToClipboard}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#c1c1c1'
                }}
                title="Click to copy"
              >
                {wallet}
              </span>
              <button
                onClick={copyToClipboard}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginLeft: '0.1rem',
                  fontSize: '1rem'
                }}
                title="Copy to clipboard"
              >
                📋
              </button>
              {copied && (
                <span style={{ marginLeft: '0.5rem', color: 'green', fontSize: '0.9rem' }}>
                  Copied!
                </span>
              )}
            </>
          ) : (
            <>
              Displaying NFTs for wallet: {' '}
              <span
                onClick={copyToClipboard}
                style={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#c1c1c1'
                }}
                title="Click to copy"
              >
                {wallet}
              </span>
              <button
                onClick={copyToClipboard}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginLeft: '0.1rem',
                  fontSize: '1rem'
                }}
                title="Copy to clipboard"
              >
                📋
              </button>
              {copied && (
                <span style={{ marginLeft: '0.5rem', color: 'green', fontSize: '0.9rem' }}>
                  Copied!
                </span>
              )}
            </>
          )}
        </p>
      </div>



      {/* ── Floating Upgrade / Craft Button ── */}
      {(() => {
        const canShowUpgrade = (
          !isRecipeFlow &&
          selectedNFTs.length === 1 &&
          selectedNFTs[0].upgradeable &&
          !selectedNFTs[0].isMultiUpgradeable
        );

        const canShowCraft = isRecipeFlow && matchedRecipe;

        if (!canShowUpgrade && !canShowCraft) return null;

        return (
          <div className="floating-button">
            <button
              onClick={() => {
                if (canShowCraft) {
                  setRecipeToCraft(matchedRecipe);
                  setShowRecipeModal(true);
                } else if (canShowUpgrade) {
                  setShowUpgradeModal(true);
                }
              }}
              className="rainbowBtn"
            >
              {canShowCraft
                ? `CRAFT ${matchedRecipe.title}`
                : `UPGRADE NFT`}
            </button>
          </div>
        );
      })()}


      {/* ── Single-Upgrade Modal ── */}
      {!isRecipeFlow && showUpgradeModal && (
        <UpgradeModal
          selectedNFTs={selectedNFTs}
          onClose={() => setShowUpgradeModal(false)}
          // only fire the burn+mint; modal stays open for confirm→upgrading→complete
          onConfirm={confirmUpgrade}
          // runs when you click "Okay" in the ✅ screen
          onDone={() => {
            setShowUpgradeModal(false)
            setSelectedNFTs([])
          }}
          confirmTitle={`Upgrade ${selectedNFTs.length} NFT${selectedNFTs.length > 1 ? 's' : ''}`}
          confirmMessage={`Are you sure you want to burn ${selectedNFTs.length} NFT${selectedNFTs.length > 1 ? 's' : ''} to mint new one${selectedNFTs.length > 1 ? 's' : ''}?`}
          confirmButtonText={`UPGRADE NFT${selectedNFTs.length > 1 ? 's' : ''}`}
          statusUpgradingText="⏳ Burning & minting…"
          statusCompleteText="✅ Upgrade Complete!"
          completeButtonText="Okay"
          danger={false}
        />
      )}

      {/* ── Recipe Modal (for both regular recipes and free mints) ── */}
      {showRecipeModal && recipeToCraft && (
        <RecipeModal
          recipe={recipeToCraft}
          selectedNFTs={selectedNFTs || []}
          onClose={() => setShowRecipeModal(false)}
          onConfirm={async () => {
            const { success, minted, minted_item, random_value, rng_pool } = await handleRecipeMint(recipeToCraft);
            return { success, minted, minted_item, random_value, rng_pool };
          }}
          onDone={async () => {
            setShowRecipeModal(false);
            setSelectedNFTs([]);
            
            // Refresh token availability and statistics after claiming
            if (recipeToCraft?.recipe_type === 'free_mint') {
              try {
                const isAvailable = await checkTokenAvailability(recipeToCraft, nfts);
                const stats = await getClaimStatistics(recipeToCraft, nfts);
                
                setTokenAvailability(prev => ({
                  ...prev,
                  [recipeToCraft.id]: isAvailable
                }));
                
                setClaimStatistics(prev => ({
                  ...prev,
                  [recipeToCraft.id]: stats
                }));
              } catch (error) {
                console.error(`Error refreshing availability for recipe ${recipeToCraft.id}:`, error);
              }
            }
          }}
          confirmTitle={recipeToCraft.recipe_type === 'free_mint' ? `Free Mint: ${recipeToCraft.title}` : `Craft ${recipeToCraft.title}`}
          confirmMessage={recipeToCraft.recipe_type === 'free_mint' 
            ? `Claim your free ${recipeToCraft.title}?`
            : `Burn ${selectedNFTs.length} NFT${selectedNFTs.length > 1 ? 's' : ''} to craft ${recipeToCraft.title}?`
          }
          confirmButtonText={recipeToCraft.recipe_type === 'free_mint' ? 'FREE MINT' : 'CRAFT'}
          statusUpgradingText={recipeToCraft.recipe_type === 'free_mint' ? '⏳ Minting…' : '⏳ Burning & crafting…'}
          statusCompleteText={recipeToCraft.recipe_type === 'free_mint' ? '✅ Free Mint Complete!' : '✅ Craft Complete!'}
          completeButtonText="Awesome"
          danger={false}
        />
      )}

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalConfig.title}
        message={errorModalConfig.message}
        type={errorModalConfig.type}
      />

      {/* DCL Purchase Modal */}
      {showDCLModal && selectedDCLDeal && (
        <DCLPurchaseModal
          deal={selectedDCLDeal}
          quantity={selectedDCLQuantity}
          onError={(error) => {
            console.error('DCL Purchase Error:', error);
            showUserFriendlyError(error, 'error');
          }}
          onClose={() => {
            setShowDCLModal(false);
            setSelectedDCLDeal(null);
            setSelectedDCLQuantity(1);
          }}
          onConfirm={async () => {
            return await handleDCLPurchase(selectedDCLDeal, selectedDCLQuantity);
          }}
          onDone={() => {
            setShowDCLModal(false);
            setSelectedDCLDeal(null);
            setSelectedDCLQuantity(1);
          }}
        />
      )}

      {/* Faucet Modal */}
      <FaucetModal
        isOpen={showFaucetModal}
        onClose={() => {
          setShowFaucetModal(false);
          fetchFaucetStatus(); // Refresh status after modal closes
        }}
        faucetConfig={faucetConfig}
        walletAddress={wallet}
        selectedWalletType={selectedWalletType}
        walletManager={walletManager}
        ownedNFTs={nfts}
        hasAccess={checkTokenGateAccess(
          faucetConfig?.token_gate_requirements || [],
          faucetConfig?.token_gate_urn_requirements || [],
          faucetConfig?.token_gate_logic || 'AND',
          nfts
        )}
        onError={(errorConfig) => {
          setErrorModalConfig(errorConfig);
          setShowErrorModal(true);
        }}
      />

      {/* Lookup Panel */}
      <div className={`slide-out-panel lookup-panel ${showLookupPanel ? 'open' : ''}`}>
        <div className="panel-header">
          <div className="panel-title">🔍 Claim Lookup</div>
          <button 
            className="panel-close"
            onClick={() => setShowLookupPanel(false)}
          >
            ×
          </button>
        </div>
        <div className="panel-content">
          <LookupTool 
            wallet={wallet}
            collectionItems={collectionItems}
            upgrades={upgrades}
            deals={deals}
            faucetConfig={faucetConfig}
            faucetStatus={faucetStatus}
            onError={(errorConfig) => {
              setErrorModalConfig(errorConfig);
              setShowErrorModal(true);
            }}
          />
        </div>
      </div>

      {/* Background Music Controls - Desktop Only */}
      {!isMobile && (
      <div className="music-controls">
        <div 
          className={`music-toggle ${isMuted ? 'muted' : ''}`}
          onClick={toggleMute}
          title={isMuted ? 'Unmute Background Music' : 'Mute Background Music'}
        >
          {isMuted ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 12C16.5 10.23 15.5 8.71 14 7.97V9.18L16.45 11.63C16.48 11.86 16.5 12.08 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" fill="white"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px' }}>
              <path d="M8 5V19L19 12L8 5Z" fill="white"/>
            </svg>
          )}
        </div>
        
        <div className="volume-controls">
          <button 
            className="volume-btn"
            onClick={decreaseVolume}
            disabled={volume <= 0}
            title="Decrease Volume"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H5V11H19V13Z" fill="white"/>
            </svg>
          </button>
          <button 
            className="volume-btn"
            onClick={increaseVolume}
            disabled={volume >= 1}
            title="Increase Volume"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white"/>
            </svg>
          </button>
        </div>
        
        <div className="volume-display">
          {Math.round(volume * 100)}%
        </div>
      </div>
      )}

    </div>
  );
}


export default NFTDashboard;
